import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import api from '../services/api'
import AvisoUltimaNota from '../components/AvisoUltimaNota'
import NovoClienteModal from '../components/NovoClienteModal'
import VeiculoModal from '../components/VeiculoModal'
import FichaViagemModal from '../components/FichaViagemModal'
import NaturezaSelect from '../components/NaturezaSelect'
import ItemSelect from '../components/ItemSelect'
import IbsCbsSelect from '../components/IbsCbsSelect'
import CidadeSelect from '../components/CidadeSelect'
import { useEmpresa } from '../contexts/EmpresaContext'

interface Empresa {
  empcod: number
  empnom: string
  empfan: string
  empcgc: string
  empest: string
  empsigla: string
}

const UF_IBGE: Record<string, string> = {
  AC:'12', AL:'27', AP:'16', AM:'13', BA:'29', CE:'23', DF:'53', ES:'32',
  GO:'52', MA:'21', MT:'51', MS:'50', MG:'31', PA:'15', PB:'25', PR:'41',
  PE:'26', PI:'22', RJ:'33', RN:'24', RS:'43', RO:'11', RR:'14', SC:'42',
  SP:'35', SE:'28', TO:'17',
}

function nowLocalDateTimeInput(): string {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

// Input type="date"/"datetime-local" deixa digitar mais de 4 dígitos no ano
// (o segmento de ano do widget nativo não tem limite) — rejeita o valor nesse caso.
function limitarAno4Digitos(valorAtual: string, valorNovo: string) {
  const ano = valorNovo.split('-')[0]
  return ano && ano.length > 4 ? valorAtual : valorNovo
}

function calcDV(chave43: string): number {
  const pesos = [2, 3, 4, 5, 6, 7, 8, 9]
  let soma = 0
  for (let i = chave43.length - 1, p = 0; i >= 0; i--, p++) {
    soma += parseInt(chave43[i]) * pesos[p % 8]
  }
  const resto = soma % 11
  return resto < 2 ? 0 : 11 - resto
}

function gerarChave(emp: Empresa, dataEmissao: string, modelo: string, serie: string, numero: string, cNF: string): string {
  const cuf  = UF_IBGE[emp.empest.trim()] ?? '43'
  const aamm = dataEmissao.replace(/-/g, '').slice(2, 6)
  const cnpj = emp.empcgc.replace(/\D/g, '').padStart(14, '0')
  const mod  = modelo.padStart(2, '0')
  const ser  = serie.padStart(3, '0')
  const num  = numero.padStart(9, '0')
  const chave43 = `${cuf}${aamm}${cnpj}${mod}${ser}${num}1${cNF}`
  return chave43 + calcDV(chave43)
}

// Compra governamental do CT-e OS (NT 2025.001 v1.14 — grupo ide/gCompraGov)
interface CompraGovForm { ativo: boolean; tpEnteGov: string; pRedutor: string; tpOperGov: string; refDFeAnt: string; doCadastro?: boolean }
const compraGovVazio: CompraGovForm = { ativo: false, tpEnteGov: '', pRedutor: '0', tpOperGov: '1', refDFeAnt: '' }
const TP_ENTE_GOV: Record<string, string> = { '1': 'União', '2': 'Estado', '3': 'Distrito Federal', '4': 'Município', '5': 'Consórcio Público', '6': 'Comitê Gestor do IBS' }
const TP_OPER_GOV: Record<string, string> = {
  '1': 'Fornecimento com pagamento posterior',
  '2': 'Recebimento do pagamento com fornecimento já realizado',
  '3': 'Fornecimento com pagamento já realizado',
  '4': 'Recebimento do pagamento com fornecimento posterior',
}

interface Cliente {
  cli_cod: string
  cli_mat: string
  cli_nom: string
  cli_fan: string
  cli_fj: string
  cli_ins: string
  cli_est: string
  cli_contri: string
  cli_cidade?: string
  cli_logradouro?: string
  cli_numero?: string
  cli_bairro?: string
  clifrona1?: number
  clifronat?: number
  clifrona2?: number
  bloqueado?: boolean
}

interface Endereco {
  logradouro: string
  numero: string
  complemento?: string
  bairro: string
  cep: string
  telefone: string
  email: string
  cidade: string
  uf: string
}

function formatarDocumento(doc: string): string {
  const n = doc.replace(/\D/g, '')
  if (n.length === 14) return n.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
  if (n.length === 11) return n.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
  return doc
}

function formatarCep(cep: string): string {
  const n = cep.replace(/\D/g, '').padStart(8, '0')
  return n.replace(/(\d{5})(\d{3})/, '$1-$2')
}

interface Natureza {
  tno_cod: number; tno_cdf: number; tno_nat: string; tno_loc: string; tno_des: string; tno_dsc: string
  tno_tic: string; tno_bic: number; tno_aic: number
  tno_tpi: string; tno_bpi: number; tno_api: number; tno_rip: string
  tno_tcf: string; tno_bcf: number; tno_acf: number
  tno_tin: string; tno_bin: number; tno_ain: number; tno_rin: string
  tno_tis: string; tno_bis: number; tno_ais: number; tno_ris: string
  tno_tir: string; tno_bir: number; tno_air: number; tno_rir: string
  tno_tcs: string; tno_bcs: number; tno_acs: number
}

interface Impostos {
  icmsCst: string;   icmsBase: number;   icmsAliq: number;   icmsDest: number
  stBase: number;    stAliq: number;     stDest: number
  ipiCst: string;    ipiBase: number;    ipiAliq: number;    ipiDest: number
  issqnBase: number; issqnAliq: number;  issqnDest: number;  issqnRet: number
  inssBase: number;  inssAliq: number;   inssDest: number;   inssRet: number
  irrfBase: number;  irrfAliq: number;   irrfDest: number;   irrfRet: number
  pisCst: string;    pisBase: number;    pisAliq: number;    pisDest: number;   pisRet: number
  cofinsCst: string; cofinsBase: number; cofinsAliq: number; cofinsDest: number; cofinsRet: number
  csllBase: number;  csllAliq: number;   csllDest: number;   csllRet: number
}

interface IbsCbs {
  cst: string; classTrib: string; classTribDes: string
  cbsBc: number; cbsBcEf: number; cbsAliRef: number; cbsRedAli: number; cbsAliEf: number; cbsValor: number
  ibsUfEst: string; ibsUfBcEf: number; ibsUfAliRef: number; ibsUfRedAli: number; ibsUfAliEf: number; ibsUfValor: number
  ibsMunBcEf: number; ibsMunAliRef: number; ibsMunRedAli: number; ibsMunAliEf: number; ibsMunValor: number
  isSbas: number; isSali: number
}

const ibsCbsVazio: IbsCbs = {
  cst: '', classTrib: '', classTribDes: '',
  cbsBc: 0, cbsBcEf: 0, cbsAliRef: 0, cbsRedAli: 0, cbsAliEf: 0, cbsValor: 0,
  ibsUfEst: '', ibsUfBcEf: 0, ibsUfAliRef: 0, ibsUfRedAli: 0, ibsUfAliEf: 0, ibsUfValor: 0,
  ibsMunBcEf: 0, ibsMunAliRef: 0, ibsMunRedAli: 0, ibsMunAliEf: 0, ibsMunValor: 0,
  isSbas: 0, isSali: 0,
}

function calcIbsCbs(total: number, r: any): IbsCbs {
  if (!r || total <= 0) return { ...ibsCbsVazio }
  const r2 = (n: number) => Math.round(n * 100) / 100
  const bcEfC = r2(total * (1 - Number(r.ibs08_crbc) / 100))
  const bcEfU = r2(total * (1 - Number(r.ibs08_urbc) / 100))
  const bcEfM = r2(total * (1 - Number(r.ibs08_mrbc) / 100))
  return {
    cst: r.ibs08_cst?.trim() ?? '',
    classTrib: r.ibs08_tri?.trim() ?? '',
    classTribDes: r.ibs08_d02?.trim() ?? '',
    cbsBc: total, cbsBcEf: bcEfC,
    cbsAliRef: Number(r.ibs08_cali), cbsRedAli: Number(r.ibs08_cpra),
    cbsAliEf: Number(r.ibs08_caef), cbsValor: r2(bcEfC * Number(r.ibs08_caef) / 100),
    ibsUfEst: r.ibs08_uest?.trim() ?? '', ibsUfBcEf: bcEfU,
    ibsUfAliRef: Number(r.ibs08_uali), ibsUfRedAli: Number(r.ibs08_upra),
    ibsUfAliEf: Number(r.ibs08_uaef), ibsUfValor: r2(bcEfU * Number(r.ibs08_uaef) / 100),
    ibsMunBcEf: bcEfM,
    ibsMunAliRef: Number(r.ibs08_mali), ibsMunRedAli: Number(r.ibs08_mpra),
    ibsMunAliEf: Number(r.ibs08_maef), ibsMunValor: r2(bcEfM * Number(r.ibs08_maef) / 100),
    isSbas: Number(r.ibs08_sbas), isSali: Number(r.ibs08_sali),
  }
}

interface Item {
  itsCod: string
  descricao: string
  complemento: string
  unidade: string
  quantidade: number
  valorUnitario: number
  ibsCbsRegra: string
  ibsCbsDes: string
  ibsCbsMunIbge: string
  cidOrigemCod: string
  cidOrigemDes: string
  cidOrigemIbge: string
  cidOrigemUf: string
  cidDestinoCod: string
  cidDestinoDes: string
  cidDestinoIbge: string
  cidDestinoUf: string
  impostos: Impostos
  ibsCbs: IbsCbs
}

interface Props {
  tipo: 'NFE' | 'CTE'
  titulo: string
}

const impostosVazio: Impostos = {
  icmsCst: '', icmsBase: 0, icmsAliq: 0, icmsDest: 0,
  stBase: 0, stAliq: 0, stDest: 0,
  ipiCst: '', ipiBase: 0, ipiAliq: 0, ipiDest: 0,
  issqnBase: 0, issqnAliq: 0, issqnDest: 0, issqnRet: 0,
  inssBase: 0, inssAliq: 0, inssDest: 0, inssRet: 0,
  irrfBase: 0, irrfAliq: 0, irrfDest: 0, irrfRet: 0,
  pisCst: '', pisBase: 0, pisAliq: 0, pisDest: 0, pisRet: 0,
  cofinsCst: '', cofinsBase: 0, cofinsAliq: 0, cofinsDest: 0, cofinsRet: 0,
  csllBase: 0, csllAliq: 0, csllDest: 0, csllRet: 0,
}

function r2(n: number) { return Math.round(n * 100) / 100 }
const f2 = (n: number) => (n || 0).toFixed(2)

function calcImpostos(total: number, nat: Natureza | undefined): Impostos {
  if (!nat || total <= 0) return { ...impostosVazio }
  const bc  = (base: number, p: number) => r2(base * (Number(p) / 100))
  const det = (base: number, aliq: number) => r2(base * (Number(aliq) / 100))
  const icmsBase   = bc(total, nat.tno_bic);  const icmsDest   = det(icmsBase,   nat.tno_aic)
  const baseReduz  = r2(total - icmsDest)
  const issqnBase  = bc(total, nat.tno_bis);  const issqnDest  = det(issqnBase,  nat.tno_ais)
  const inssBase   = bc(total, nat.tno_bin);  const inssDest   = det(inssBase,   nat.tno_ain)
  const irrfBase   = bc(total, nat.tno_bir);  const irrfDest   = det(irrfBase,   nat.tno_air)
  const pisBase    = bc(baseReduz, nat.tno_bpi);  const pisDest    = det(pisBase,    nat.tno_api)
  const cofinsBase = bc(baseReduz, nat.tno_bcf);  const cofinsDest = det(cofinsBase, nat.tno_acf)
  const csllBase   = bc(total, nat.tno_bcs);  const csllDest   = det(csllBase,   nat.tno_acs)
  return {
    icmsCst: nat.tno_tic,   icmsBase,   icmsAliq: Number(nat.tno_aic),  icmsDest,
    stBase: 0, stAliq: 0, stDest: 0,
    ipiCst: '', ipiBase: 0, ipiAliq: 0, ipiDest: 0,
    issqnBase, issqnAliq: Number(nat.tno_ais), issqnDest, issqnRet: nat.tno_ris?.trim() === 'S' ? issqnDest : 0,
    inssBase,  inssAliq:  Number(nat.tno_ain), inssDest,  inssRet:  nat.tno_rin?.trim() === 'S' ? inssDest  : 0,
    irrfBase,  irrfAliq:  Number(nat.tno_air), irrfDest,  irrfRet:  nat.tno_rir?.trim() === 'S' ? irrfDest  : 0,
    pisCst: nat.tno_tpi,    pisBase,    pisAliq: Number(nat.tno_api),    pisDest,    pisRet: nat.tno_rip?.trim() === 'S' ? pisDest : 0,
    // COFINS não tem flag de retenção própria na natureza — acompanha o mesmo flag do PIS (tno_rip)
    cofinsCst: nat.tno_tcf, cofinsBase, cofinsAliq: Number(nat.tno_acf), cofinsDest, cofinsRet: nat.tno_rip?.trim() === 'S' ? cofinsDest : 0,
    csllBase,  csllAliq:  Number(nat.tno_acs), csllDest,  csllRet:  0,
  }
}

const ETAPAS = ['Cliente', 'Dados', 'Itens', 'Viagem', 'Revisão']

export default function EmitirNota({ tipo, titulo }: Props) {
  const navigate = useNavigate()
  const { empresaCod: empresaCodGlobal } = useEmpresa()
  const [searchParams] = useSearchParams()
  const editId = searchParams.get('editId') ? Number(searchParams.get('editId')) : null
  const copyId = searchParams.get('copyId') ? Number(searchParams.get('copyId')) : null

  const [etapa, setEtapa] = useState(0)
  const [chaveOriginal, setChaveOriginal] = useState('')
  const [carregandoEdit, setCarregandoEdit] = useState(false)
  const [editEmpcod, setEditEmpcod] = useState<number | null>(null)

  // Etapa 0 — cliente
  const [busca, setBusca] = useState('')
  const [clientes, setClientes] = useState<Cliente[]>([])
  const [clienteSelecionado, setClienteSelecionado] = useState<Cliente | null>(null)
  const [buscando, setBuscando] = useState(false)
  const [modalNovo, setModalNovo] = useState(false)
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Endereço/contato do cliente (carregado após seleção)
  const [endereco, setEndereco] = useState<Endereco | null>(null)
  const [editando, setEditando] = useState(false)
  const [endEdit, setEndEdit] = useState<Endereco>({ logradouro: '', numero: '', bairro: '', cep: '', telefone: '', email: '', cidade: '', uf: '' })

  // Empresa emitente
  const [empresas, setEmpresas] = useState<Empresa[]>([])
  const [empresa, setEmpresa] = useState<Empresa | null>(null)
  const [cNF, setCNF] = useState('')

  // Etapa 1 — dados
  const [naturezas, setNaturezas] = useState<Natureza[]>([])
  const [natureza, setNatureza] = useState('')
  const [dataEmissao, setDataEmissao] = useState(new Date().toISOString().slice(0, 10))
  const [serie, setSerie] = useState('')
  const [numero, setNumero] = useState('')
  const [carregandoNum, setCarregandoNum] = useState(false)
  const [obs, setObs] = useState('')
  const [caracAd, setCaracAd] = useState('') // CT-e OS: Carac. Adicionais (xCaracSer)
  // CT-e OS: compra governamental (gCompraGov) — liga sozinho quando o tomador é ente governamental no cadastro
  const [compraGov, setCompraGov] = useState<CompraGovForm>(compraGovVazio)
  const [errosDados, setErrosDados] = useState<string[]>([])
  const [finalidade, setFinalidade] = useState(tipo === 'CTE' ? 'CTO' : '')
  const [numeroViagem, setNumeroViagem] = useState('')
  const [modalFichaViagem, setModalFichaViagem] = useState(false)

  // Dados da viagem (CT-e OS)
  const [tipoViagem, setTipoViagem] = useState('0')
  const [tipoServico, setTipoServico] = useState('6')
  const [dhViagem, setDhViagem] = useState('')
  const [veiCod, setVeiCod] = useState('')
  const [veiDados, setVeiDados] = useState<{ veipla: string; veiren: string; veiestado: string; veinpa: number } | null>(null)
  const [modalVeiculo, setModalVeiculo] = useState(false)
  const [veiPlaca, setVeiPlaca] = useState('')
  const [veiRenavan, setVeiRenavan] = useState('')
  const [veiUf, setVeiUf] = useState('')
  const [qtdPassageiros, setQtdPassageiros] = useState('')
  const [descServico, setDescServico] = useState('')
  const [estadosViagem, setEstadosViagem] = useState('')
  const [tipoNota, setTipoNota] = useState('1')
  const [operacao, setOperacao] = useState('S')
  const [modal, setModal] = useState('')
  const [tipoConsumidor, setTipoConsumidor] = useState('0')
  const [presencaConsumidor, setPresencaConsumidor] = useState('')
  const [modeloFiscal, setModeloFiscal] = useState(tipo === 'CTE' ? '67' : '')
  const [chaveNfe, setChaveNfe] = useState('')

  // Etapa 2 — itens
  const itemVazio: Item = { itsCod: '', descricao: '', complemento: '', unidade: 'UN', quantidade: 1, valorUnitario: 0, ibsCbsRegra: '', ibsCbsDes: '', ibsCbsMunIbge: '', cidOrigemCod: '', cidOrigemDes: '', cidOrigemIbge: '', cidOrigemUf: '', cidDestinoCod: '', cidDestinoDes: '', cidDestinoIbge: '', cidDestinoUf: '', impostos: { ...impostosVazio }, ibsCbs: { ...ibsCbsVazio } }
  const [itens, setItens] = useState<Item[]>([{ ...itemVazio }])

  const [itemExpandido, setItemExpandido] = useState<number | null>(null)
  const [abaFiscal, setAbaFiscal] = useState<Record<number, 'impostos' | 'ibscbs'>>({})
  const [tentouAvancarItens, setTentouAvancarItens] = useState(false)
  const [tentouAvancarViagem, setTentouAvancarViagem] = useState(false)

  // Financeiro
  const [financeiroModal, setFinanceiroModal] = useState(false)
  const [finQtdParcelas, setFinQtdParcelas] = useState(1)
  const [finIntervalo, setFinIntervalo] = useState(0)
  const [finCondDes, setFinCondDes] = useState('')
  const [finParcelas, setFinParcelas] = useState<{ numero: string; vencimento: string; valor: string }[]>([])
  const [finContaCod, setFinContaCod] = useState('')
  const [finContaNome, setFinContaNome] = useState('')
  const [finContaBusca, setFinContaBusca] = useState('')
  const [finContaResultados, setFinContaResultados] = useState<{ dep_cod: string; dep_des: string }[]>([])
  const [finContaAberto, setFinContaAberto] = useState(false)
  const [finContaErro, setFinContaErro] = useState<string | null>(null)

  // Resultado
  const [enviando, setEnviando] = useState(false)
  const [resultado, setResultado] = useState<string | null>(null)
  const [erro, setErro] = useState<string | null>(null)

  useEffect(() => {
    api.get('/erp/empresas').then(r => {
      setEmpresas(r.data)
      const padrao = r.data.find((e: Empresa) => e.empcod === empresaCodGlobal) ?? r.data[0]
      if (padrao) setEmpresa(padrao)
    }).catch(() => {})
    api.get('/erp/naturezas').then(r => setNaturezas(r.data)).catch(() => {})
  }, [])

  // Seleciona empresa correta ao carregar em modo edição
  useEffect(() => {
    if (editEmpcod !== null && empresas.length > 0) {
      const emp = empresas.find(e => e.empcod === editEmpcod)
      if (emp) setEmpresa(emp)
    }
  }, [editEmpcod, empresas])

  // Carrega payload do banco ao abrir em modo edição
  useEffect(() => {
    if (!editId) return
    setCarregandoEdit(true)
    api.get(`/cte-os/${editId}/payload`).then(r => {
      const p = r.data
      setChaveOriginal(p.chave)
      setCNF(p.cNF)
      setSerie(p.serie)
      setNumero(String(p.numero))
      setEditEmpcod(p.empcod ?? 1)
      setClienteSelecionado(p.clienteSelecionado)
      setBusca(p.clienteSelecionado?.cli_nom || '')
      // O payload guarda a "foto" do cliente de quando a nota foi gerada. Numa
      // reedição de nota rejeitada (ex: IE cadastrada depois da rejeição), precisa
      // buscar o cliente de novo pra não reenviar o dado velho (mesmo bug corrigido
      // ontem em EmitirNFeVenda.tsx).
      const cnpjClienteEdit = p.clienteSelecionado?.cli_mat?.replace(/\D/g, '')
      if (cnpjClienteEdit) {
        api.get(`/erp/clientes?busca=${cnpjClienteEdit}`).then(r => {
          // mesmo CNPJ pode ter vários cadastros: mantém o cli_cod escolhido na emissão
          const atual = r.data.find((c: Cliente) => c.cli_cod === p.clienteSelecionado?.cli_cod)
            ?? r.data.find((c: Cliente) => c.cli_mat?.replace(/\D/g, '') === cnpjClienteEdit)
          if (atual) setClienteSelecionado(atual)
        }).catch(() => {})
      }
      if (p.endereco) { setEndereco(p.endereco); setEndEdit(p.endereco) }
      setNatureza(String(p.naturezaCod))
      setDataEmissao(p.dataEmissao || new Date().toISOString().slice(0, 10))
      setFinalidade(p.finalidade || 'CTO')
      setModal(p.modal ? String(p.modal) : '')
      setTipoNota(p.tipoNota || '1')
      setObs(p.obs || '')
      setCaracAd(p.caracAd || '')
      setCompraGov(p.compraGov ? { ...compraGovVazio, ...p.compraGov } : compraGovVazio)
      setTipoViagem(p.tipoViagem || '0')
      setTipoServico(p.tipoServico || '6')
      setDhViagem(p.dhViagem || '')
      setTipoConsumidor(p.tipoConsumidor || '0')
      setPresencaConsumidor(p.presencaConsumidor || '')
      setVeiCod(p.veiCod || '')
      if (p.veiDados) {
        setVeiDados(p.veiDados)
        setVeiPlaca(p.veiDados.veipla || '')
        setVeiRenavan(p.veiDados.veiren || '')
        setVeiUf(p.veiDados.veiestado || '')
        setQtdPassageiros(String(p.veiDados.veinpa || ''))
      }
      setDescServico(p.descServico || '')
      setEstadosViagem(p.estadosViagem || '')
      setNumeroViagem(p.numeroViagem || '')
      if (p.itens && p.itens.length > 0) setItens(p.itens)
      setEtapa(1)
    }).catch(() => {}).finally(() => setCarregandoEdit(false))
  }, [editId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Carrega payload do banco ao abrir em modo cópia — igual à edição, mas sem
  // chave/série/número (ganham valores novos automaticamente) nem vínculo com a nota original
  useEffect(() => {
    if (!copyId) return
    setCarregandoEdit(true)
    api.get(`/cte-os/${copyId}/payload`).then(r => {
      const p = r.data
      setCNF(p.cNF)
      setEditEmpcod(p.empcod ?? 1)
      setClienteSelecionado(p.clienteSelecionado)
      setBusca(p.clienteSelecionado?.cli_nom || '')
      // Mesma correção da edição — cópia também não pode reenviar cadastro velho do cliente.
      const cnpjClienteCopia = p.clienteSelecionado?.cli_mat?.replace(/\D/g, '')
      if (cnpjClienteCopia) {
        api.get(`/erp/clientes?busca=${cnpjClienteCopia}`).then(r => {
          const atual = r.data.find((c: Cliente) => c.cli_cod === p.clienteSelecionado?.cli_cod)
            ?? r.data.find((c: Cliente) => c.cli_mat?.replace(/\D/g, '') === cnpjClienteCopia)
          if (atual) setClienteSelecionado(atual)
        }).catch(() => {})
      }
      if (p.endereco) { setEndereco(p.endereco); setEndEdit(p.endereco) }
      setNatureza(String(p.naturezaCod))
      setDataEmissao(new Date().toISOString().slice(0, 10))
      setFinalidade(p.finalidade || 'CTO')
      setModal(p.modal ? String(p.modal) : '')
      setTipoNota(p.tipoNota || '1')
      setObs(p.obs || '')
      setCaracAd(p.caracAd || '')
      setCompraGov(p.compraGov ? { ...compraGovVazio, ...p.compraGov } : compraGovVazio)
      setTipoViagem(p.tipoViagem || '0')
      setTipoServico(p.tipoServico || '6')
      setDhViagem(p.dhViagem || '')
      setTipoConsumidor(p.tipoConsumidor || '0')
      setPresencaConsumidor(p.presencaConsumidor || '')
      setVeiCod(p.veiCod || '')
      if (p.veiDados) {
        setVeiDados(p.veiDados)
        setVeiPlaca(p.veiDados.veipla || '')
        setVeiRenavan(p.veiDados.veiren || '')
        setVeiUf(p.veiDados.veiestado || '')
        setQtdPassageiros(String(p.veiDados.veinpa || ''))
      }
      setDescServico(p.descServico || '')
      setEstadosViagem(p.estadosViagem || '')
      setNumeroViagem(p.numeroViagem || '')
      if (p.itens && p.itens.length > 0) setItens(p.itens)
      setEtapa(1)
    }).catch(() => {}).finally(() => setCarregandoEdit(false))
  }, [copyId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Recalcula impostos de todos os itens quando muda natureza
  useEffect(() => {
    const nat = naturezas.find(n => String(n.tno_cod) === natureza)
    if (!nat) return
    setItens(prev => prev.map(it => ({
      ...it,
      impostos: calcImpostos(it.quantidade * it.valorUnitario, nat),
    })))
  }, [natureza, naturezas])

  // Auto-seleciona natureza pelo CFOP configurado no cliente ao mudar o modal
  useEffect(() => {
    if (!modal || !clienteSelecionado) return
    const cfopMap: Record<string, number | undefined> = {
      '2': clienteSelecionado.clifrona1 ? Number(clienteSelecionado.clifrona1) : undefined,
      '1': clienteSelecionado.clifronat  ? Number(clienteSelecionado.clifronat)  : undefined,
      '3': clienteSelecionado.clifrona2  ? Number(clienteSelecionado.clifrona2)  : undefined,
    }
    const cfop = cfopMap[modal]
    if (!cfop) return
    const nat = naturezas.find(n => n.tno_cod === cfop)
    if (nat) setNatureza(String(nat.tno_cod))
  }, [modal, clienteSelecionado, naturezas])

  // Carrega série e número ao entrar na etapa de dados, gera cNF aleatório
  useEffect(() => {
    if (etapa !== 1 || numero) return
    setCarregandoNum(true)
    const novoCNF = String(Math.floor(Math.random() * 99999999)).padStart(8, '0')
    setCNF(novoCNF)
    api.get(`/erp/proximonumero/${empresa?.empcod ?? 1}/${tipo}`)
      .then(r => { setSerie(r.data.serie); setNumero(String(r.data.numero)) })
      .catch(() => { setSerie('1') })
      .finally(() => setCarregandoNum(false))
  }, [etapa])

  // Gera chave automaticamente quando todos os campos estão disponíveis
  useEffect(() => {
    if (!empresa || !dataEmissao || !modeloFiscal || !serie || !numero || !cNF) return
    setChaveNfe(gerarChave(empresa, dataEmissao, modeloFiscal, serie, numero, cNF))
  }, [empresa, dataEmissao, modeloFiscal, serie, numero, cNF])

  // Nº da Viagem informado (CT-e) — busca a ficha e auto-preenche Tipo de
  // Serviço (sempre Eventual quando há ficha) e o veículo escalado
  useEffect(() => {
    if (tipo !== 'CTE' || !numeroViagem) return
    let cancelado = false
    const t = setTimeout(() => {
      api.get(`/cte-os/fichas-viagem/${numeroViagem}`).then(r => {
        if (cancelado) return
        setTipoServico('6')
        setObs(prev => {
          const linha = `Ficha de viagem: ${numeroViagem}`
          const semFicha = prev.replace(/^Ficha de viagem: \d+\n?/, '')
          if (semFicha === prev && prev.includes(linha)) return prev
          return semFicha ? `${linha}\n${semFicha}` : linha
        })
        const veicod = r.data?.veicod
        if (veicod) {
          setVeiCod(veicod)
          api.get(`/erp/veiculos/${veicod}`).then(rv => {
            if (cancelado || !rv.data) return
            setVeiDados(rv.data)
            setVeiPlaca(rv.data.veipla || '')
            setVeiRenavan(rv.data.veiren || '')
            setVeiUf(rv.data.veiestado || '')
            setQtdPassageiros(String(rv.data.veinpa || ''))
          }).catch(() => {})
        }
      }).catch(() => {})
    }, 400)
    return () => { cancelado = true; clearTimeout(t) }
  }, [numeroViagem, tipo])

  function handleBusca(valor: string) {
    setBusca(valor)
    setClienteSelecionado(null)
    setEndereco(null)
    if (debounce.current) clearTimeout(debounce.current)
    if (valor.length < 2) { setClientes([]); return }
    debounce.current = setTimeout(async () => {
      setBuscando(true)
      try {
        const r = await api.get('/erp/clientes', { params: { busca: valor } })
        setClientes(r.data)
      } finally {
        setBuscando(false)
      }
    }, 400)
  }

  async function selecionarCliente(c: Cliente) {
    setClienteSelecionado(c)
    setBusca(c.cli_nom)
    setClientes([])
    setEndereco(null)
    setEditando(false)
    try {
      const r = await api.get(`/erp/clientes/${c.cli_cod}/endereco`)
      const end: Endereco = r.data
      setEndereco(end)
      setEndEdit({ ...end })
    } catch { /* sem endereço */ }
    try {
      const r = await api.get(`/erp/clientes/${c.cli_mat}/clifro`)
      setClienteSelecionado(prev => prev ? { ...prev, ...r.data } : prev)
    } catch { /* sem clifro */ }
    if (tipo === 'CTE') {
      try {
        const rn = await api.get(`/erp/clientes-natjur/${c.cli_mat.replace(/\D/g, '')}`)
        const ente = rn.data?.tp_ente_gov ? String(rn.data.tp_ente_gov) : ''
        setCompraGov(ente ? { ...compraGovVazio, ativo: true, tpEnteGov: ente, doCadastro: true } : compraGovVazio)
      } catch { setCompraGov(compraGovVazio) }
    }
    // Observação cadastrada no cliente + vencimento (baseado na condição de pagamento) —
    // sugere no campo, mas é só o valor inicial: editar aqui não altera o cadastro do cliente.
    const [rAdicionais, rCondPgto] = await Promise.all([
      api.get(`/erp/clientes/${c.cli_mat}/adicionais`).catch(() => null),
      api.get(`/erp/clientes/${c.cli_mat}/condicao-pagamento`).catch(() => null),
    ])
    const obsCliente = rAdicionais?.data?.vnd_cod_obs_des ?? ''
    setFinIntervalo(rCondPgto?.data?.dias ?? 0)
    setFinCondDes(rCondPgto?.data?.des ?? '')
    let vencimentoTexto = ''
    if (rCondPgto?.data?.dias) {
      const [ano, mes, dia] = dataEmissao.split('-').map(Number)
      const venc = new Date(ano, mes - 1, dia)
      venc.setDate(venc.getDate() + Number(rCondPgto.data.dias))
      vencimentoTexto = `VENCIMENTO: ${venc.toLocaleDateString('pt-BR')}`
    }
    setObs([obsCliente, vencimentoTexto].filter(Boolean).join(' / '))
  }

  function salvarEdicao() {
    setEndereco({ ...endEdit })
    setEditando(false)
  }

  function addItem() {
    setItens(prev => [...prev, { ...itemVazio }])
  }

  function removeItem(i: number) {
    setItens(prev => prev.filter((_, idx) => idx !== i))
  }

  function updateItem(i: number, campo: keyof Item, valor: string | number) {
    const nat = naturezas.find(n => String(n.tno_cod) === natureza)
    setItens(prev => prev.map((it, idx) => {
      if (idx !== i) return it
      const updated = { ...it, [campo]: valor }
      if (campo === 'quantidade' || campo === 'valorUnitario') {
        const total = (campo === 'quantidade' ? Number(valor) : it.quantidade)
                    * (campo === 'valorUnitario' ? Number(valor) : it.valorUnitario)
        updated.impostos = calcImpostos(total, nat)
        if (updated.descricao && updated.quantidade > 0 && updated.valorUnitario > 0) {
          setItemExpandido(i)
        }
      }
      return updated
    }))
  }



  const totalItens = itens.reduce((s, it) => s + it.quantidade * it.valorUnitario, 0)
  const nat = naturezas.find(n => String(n.tno_cod) === natureza)

  async function emitir() {
    if (!clienteSelecionado || !natureza || !numero) return
    if (clienteSelecionado.bloqueado) { setErro('Cliente bloqueado — não é possível emitir documentos para este cliente.'); return }
    setEnviando(true)
    setErro(null)
    try {
      if (tipo === 'CTE') {
        const primeiroItem = itens[0]
        const ultimoItem   = itens[itens.length - 1]
        const chaveParaUsar = editId ? chaveOriginal : chaveNfe
        const _formPayload = {
          chave: chaveParaUsar,
          cNF,
          serie,
          numero: Number(numero),
          empcod: empresa?.empcod ?? 1,
          clienteSelecionado,
          endereco,
          naturezaCod: natureza,
          dataEmissao,
          finalidade,
          modal: modal ? Number(modal) : undefined,
          tipoNota,
          obs,
          caracAd,
          tipoViagem,
          tipoServico,
          dhViagem,
          tipoConsumidor,
          presencaConsumidor,
          veiCod,
          veiDados,
          veiPlaca,
          veiRenavan,
          veiUf,
          qtdPassageiros,
          descServico,
          estadosViagem,
          numeroViagem,
          itens,
          compraGov,
        }
        const payload = {
          _formPayload,
          empcod: empresa?.empcod ?? 1,
          chave: chaveParaUsar,
          serie,
          numero: Number(numero),
          dataEmissao,
          tomador: {
            cliCod: clienteSelecionado.cli_cod,
            doc: clienteSelecionado.cli_mat.replace(/\D/g, ''),
            fj: clienteSelecionado.cli_fj as 'J' | 'F',
            nome: clienteSelecionado.cli_nom?.trim() || clienteSelecionado.cli_fan,
            fantasia: clienteSelecionado.cli_fan?.trim() || undefined,
            ie: clienteSelecionado.cli_ins?.trim() || '',
            uf: clienteSelecionado.cli_est?.trim() || '',
            contri: clienteSelecionado.cli_contri,
            endereco: endereco ? {
              logradouro: endereco.logradouro,
              numero: endereco.numero,
              complemento: endereco.complemento,
              bairro: endereco.bairro,
              cidade: endereco.cidade,
              uf: endereco.uf,
              cep: endereco.cep,
              telefone: endereco.telefone,
              email: endereco.email,
            } : undefined,
          },
          natureza: {
            cfop: nat?.tno_cdf ?? 0,
            descricao: nat?.tno_des ?? '',
          },
          viagem: {
            tipoServico,
            cMunIni: primeiroItem.cidOrigemIbge,
            xMunIni: primeiroItem.cidOrigemDes,
            UFIni: primeiroItem.cidOrigemUf,
            cMunFim: ultimoItem.cidDestinoIbge,
            xMunFim: ultimoItem.cidDestinoDes,
            UFFim: ultimoItem.cidDestinoUf,
            estadosViagem,
            descricao: descServico,
            numeroViagem: numeroViagem || undefined,
          },
          itens: itens.map(it => ({
            itsCod: it.itsCod,
            descricao: it.descricao,
            complemento: it.complemento,
            quantidade: it.quantidade,
            valorUnitario: it.valorUnitario,
            total: it.quantidade * it.valorUnitario,
            icmsCst: it.impostos.icmsCst,
            icmsBase: it.impostos.icmsBase,
            icmsAliq: it.impostos.icmsAliq,
            icmsValor: it.impostos.icmsDest,
            pisValor: it.impostos.pisDest,
            cofinsValor: it.impostos.cofinsDest,
            inssBase: it.impostos.inssRet ? it.impostos.inssBase : 0,
            inssAliq: it.impostos.inssRet ? it.impostos.inssAliq : 0,
          })),
          totalGeral: totalItens,
          vInss: itens.reduce((s, it) => s + (it.impostos.inssRet ?? 0), 0) || undefined,
          vIrrf: itens.reduce((s, it) => s + (it.impostos.irrfRet ?? 0), 0) || undefined,
          modal: modal ? Number(modal) : undefined,
          tipoNota,
          obs,
          caracSer: caracAd.trim() || undefined,
          compraGov: compraGov.ativo ? {
            tpEnteGov: Number(compraGov.tpEnteGov),
            pRedutor: Number(String(compraGov.pRedutor).replace(',', '.')) || 0,
            tpOperGov: Number(compraGov.tpOperGov),
            refDFeAnt: compraGov.refDFeAnt.split(/[\s,;]+/).map(s => s.replace(/\D/g, '')).filter(Boolean),
          } : undefined,
        }

        const r = await api.post('/cte-os/salvar', payload)
        const res = r.data

        // Transmite imediatamente após salvar
        let autorizado = false
        try {
          const rt = await api.post(`/cte-os/${res.id}/transmitir`)
          const ret = rt.data
          if (ret.autorizado === true) {
            autorizado = true
            setResultado(`✓ Autorizado — Protocolo ${ret.protocolo}`)
          } else {
            const motivo = ret.cStat ? `cStat ${ret.cStat}: ${ret.xMotivo}` : ret.erro || 'Sem resposta do SEFAZ'
            setResultado(`REJEITADO\n\n${motivo}`)
          }
        } catch (et: any) {
          const erroDetalhe = et?.response?.data?.error || et?.message || 'Erro desconhecido'
          try {
            const rs = await api.get(`/cte-os/${res.id}/status`)
            const st = rs.data
            if (st.situacao === 'AUTORIZADA') {
              autorizado = true
              setResultado(`✓ Autorizado — Protocolo ${st.protocolo}`)
            } else {
              setResultado(`ERRO AO TRANSMITIR\n\nSituação atual: ${st.situacao}\ncStat: ${st.cstat || '—'}\n\n${erroDetalhe}`)
            }
          } catch {
            setResultado(`ERRO AO TRANSMITIR\n\n${erroDetalhe}`)
          }
        }

        if (autorizado && finParcelas.length > 0 && finContaCod) {
          const retencaoTotal = itens.reduce((s, it) =>
            s + (it.impostos.inssRet ?? 0) + (it.impostos.irrfRet ?? 0)
              + (it.impostos.pisRet  ?? 0) + (it.impostos.cofinsRet ?? 0)
              + (it.impostos.csllRet ?? 0), 0)
          try {
            await api.post(`/cte-os/${res.id}/financeiro`, {
              parcelas: finParcelas,
              contaCod: finContaCod,
              retencaoTotal,
            })
          } catch (ef: any) {
            console.warn('[Financeiro] Erro ao salvar financeiro:', ef?.response?.data?.error || ef?.message)
          }
        }

        if (autorizado) {
          setTimeout(() => navigate('/cte/lista'), 2200)
        }
        return
      }

      // NF-e (fluxo original)
      const payload = {
        header: {
          ntv_empcod: empresa?.empcod ?? 1,
          ntv_serie: serie,
          ntv_numero: Number(numero),
          cli_cod: Number(clienteSelecionado.cli_mat),
          ntv_datemi: dataEmissao,
          ntv_datsai: dataEmissao,
          ntv_vl_mer: totalItens,
          ntv_vl_tot: totalItens,
          ntv_modelo: '55',
          ftv_tipdco: tipo,
          ntv_tnocod: Number(natureza),
          ntv_infcpl: obs,
          ntv_sitnfe: 'rascunho',
        },
        items: itens.map((it, i) => ({
          pnv_lin: i + 1,
          pnv_des: it.descricao,
          pnv_un: it.unidade,
          pnv_qt_ven: it.quantidade,
          pnv_preco: it.valorUnitario,
          pnv_vl_mer: it.quantidade * it.valorUnitario,
        })),
      }
      await api.post('/nfe/emitir', payload)
      setResultado(`${titulo} gerada como rascunho — Série ${serie} / Nº ${numero}`)
      setEtapa(4)
    } catch (e: any) {
      setErro(e?.response?.data?.error || e?.response?.data?.xml ?
        `${e?.response?.data?.error || 'Erro'}\n${e?.response?.data?.dica ?? ''}` :
        'Erro ao emitir.')
    } finally {
      setEnviando(false)
    }
  }

  function reiniciar() {
    setEtapa(0); setBusca(''); setClienteSelecionado(null); setClientes([])
    setEndereco(null); setEditando(false)
    setNatureza(''); setSerie(''); setNumero(''); setObs(''); setCaracAd(''); setNumeroViagem('')
    setItens([{ ...itemVazio }])
    setChaveOriginal('')
    setResultado(null); setErro(null)
  }

  if (carregandoEdit) {
    return (
      <div className="card" style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
        Carregando dados para edição...
      </div>
    )
  }

  if (etapa === 4 && enviando) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>⏳</div>
        <h2 style={{ color: '#374151', marginBottom: '0.5rem' }}>Transmitindo para o SEFAZ...</h2>
        <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>Aguarde, isso pode levar alguns segundos.</p>
      </div>
    )
  }

  if (etapa === 4 && resultado !== null) {
    const isAutorizado = resultado.startsWith('✓')
    const isRejeicao   = resultado.startsWith('REJEITADO')
    return (
      <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>
          {isAutorizado ? '✅' : '❌'}
        </div>
        <h2 style={{ color: isAutorizado ? '#065f46' : '#dc2626', marginBottom: '0.75rem' }}>
          {isAutorizado ? 'Autorizado pelo SEFAZ!' : isRejeicao ? 'Nota Rejeitada' : 'Erro na Transmissão'}
        </h2>
        <div style={{
          background: isAutorizado ? '#ecfdf5' : '#fef2f2',
          border: `1px solid ${isAutorizado ? '#6ee7b7' : '#fca5a5'}`,
          borderRadius: '0.5rem',
          padding: '1rem 1.5rem',
          marginBottom: '1.5rem',
          display: 'inline-block',
          textAlign: 'left',
          maxWidth: 480,
          whiteSpace: 'pre-line',
          fontFamily: 'monospace',
          fontSize: '0.85rem',
          color: isAutorizado ? '#065f46' : '#7f1d1d',
          wordBreak: 'break-word',
        }}>{resultado}</div>
        {isAutorizado && <p style={{ color: '#6b7280', fontSize: '0.9rem', marginBottom: '1rem' }}>Redirecionando para a lista...</p>}
        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button className="btn btn-primary" onClick={() => navigate('/cte/lista')}>Ir para a Lista</button>
          <button className="btn btn-secondary" onClick={reiniciar}>Nova Emissão</button>
        </div>
      </div>
    )
  }

  return (
    <>
    <AvisoUltimaNota empcod={empresa?.empcod ?? 1} modelo={tipo === 'CTE' ? '67' : '55'} serie={serie} proximo={numero} onNumeroAjustado={n => setNumero(String(n))} />
    <div>
      <div className="stepper">
        {ETAPAS.map((e, i) => (
          <div key={e} className={`step ${i === etapa ? 'step--active' : i < etapa ? 'step--done' : ''}`}>
            <div className="step-circle">{i < etapa ? '✓' : i + 1}</div>
            <span>{e}</span>
          </div>
        ))}
      </div>

      <div className="card">

        {/* ── Etapa 0: Cliente ── */}
        {etapa === 0 && (
          <div>
            <h3 className="etapa-titulo" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              Selecionar Cliente
              {tipo === 'CTE' && <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#111827' }}>WNOT001</span>}
            </h3>

            {!clienteSelecionado && (
              <div className="busca-cliente-wrap">
                <div className="field" style={{ marginBottom: 0 }}>
                  <label>Nome, nome fantasia ou CNPJ/CPF</label>
                  <div className="busca-cliente-row">
                    <input
                      type="text"
                      placeholder="Digite para buscar..."
                      value={busca}
                      onChange={e => handleBusca(e.target.value)}
                      autoFocus
                    />
                    {buscando && <span className="busca-spinner">Buscando...</span>}
                    <button className="btn btn-primary btn-sm" onClick={() => setModalNovo(true)} style={{ whiteSpace: 'nowrap' }}>
                      + Novo Cliente
                    </button>
                  </div>
                </div>
              </div>
            )}

            {modalNovo && (
              <NovoClienteModal
                onClose={() => setModalNovo(false)}
                onSalvo={c => { setModalNovo(false); selecionarCliente(c) }}
              />
            )}

            {clientes.length > 0 && !clienteSelecionado && (
              <div className="lista-clientes">
                {clientes.map(c => (
                  <button key={c.cli_cod} className="item-cliente" onClick={() => selecionarCliente(c)}>
                    <div className="item-cliente-linha1">
                      <strong>{c.cli_nom}</strong>
                      <span className="badge-tipo">{c.cli_fj === 'J' ? 'PJ' : 'PF'}</span>
                    </div>
                    {c.cli_fan && c.cli_fan !== c.cli_nom && (
                      <span className="item-cliente-fantasia">{c.cli_fan}</span>
                    )}
                    <div className="item-cliente-doc">
                      <span>{c.cli_fj === 'J' ? 'CNPJ' : 'CPF'}: {formatarDocumento(c.cli_mat)}</span>
                      {!c.cli_cidade?.trim() && c.cli_est?.trim() && <span>UF: {c.cli_est}</span>}
                    </div>
                    {(c.cli_logradouro?.trim() || c.cli_cidade?.trim()) && (
                      <span className="item-cliente-endereco">
                        {[
                          [c.cli_logradouro, c.cli_numero].filter(Boolean).join(', '),
                          c.cli_bairro,
                          c.cli_cidade && c.cli_est ? `${c.cli_cidade}/${c.cli_est}` : (c.cli_cidade || c.cli_est),
                        ].filter(Boolean).join(' — ')}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}

            {clienteSelecionado && (
              <div className="cliente-card">
                {/* Cabeçalho */}
                <div className="cliente-card-header">
                  <div>
                    <div className="cliente-card-nome">{clienteSelecionado.cli_nom}</div>
                    {clienteSelecionado.cli_fan && clienteSelecionado.cli_fan !== clienteSelecionado.cli_nom && (
                      <div className="cliente-card-fantasia">{clienteSelecionado.cli_fan}</div>
                    )}
                  </div>
                </div>

                {/* Dados fiscais */}
                <div className="cliente-card-fiscal">
                  <span><b>{clienteSelecionado.cli_fj === 'J' ? 'CNPJ' : 'CPF'}:</b> {formatarDocumento(clienteSelecionado.cli_mat)}</span>
                  {clienteSelecionado.cli_ins?.trim() && <span><b>IE:</b> {clienteSelecionado.cli_ins}</span>}
                  <span><b>Contribuinte:</b> {clienteSelecionado.cli_ins?.trim() || clienteSelecionado.cli_contri !== 'N' ? 'Sim' : 'Não'}</span>
                </div>

                {clienteSelecionado.bloqueado && (
                  <div style={{ background: '#fee2e2', color: '#991b1b', fontWeight: 700, fontSize: '0.85rem', padding: '0.5rem 0.75rem', borderRadius: 4, margin: '0.5rem 0' }}>
                    ⚠ CLIENTE BLOQUEADO — não é possível emitir documentos para este cliente.
                  </div>
                )}

                {/* Endereço/contato — visualização */}
                {!editando && endereco && (
                  <div className="cliente-card-end">
                    {(endereco.logradouro?.trim() || endereco.numero?.trim()) && (
                      <div className="end-linha">
                        <span className="end-icone">📍</span>
                        <span>{[endereco.logradouro, endereco.numero, endereco.bairro].filter(Boolean).join(', ')}{endereco.cidade ? ` — ${endereco.cidade}/${endereco.uf}` : ''}{endereco.cep?.trim() ? ` · CEP ${formatarCep(endereco.cep)}` : ''}</span>
                      </div>
                    )}
                    {endereco.telefone?.trim() && (
                      <div className="end-linha">
                        <span className="end-icone">📞</span>
                        <span>{endereco.telefone}</span>
                      </div>
                    )}
                    {endereco.email?.trim() && (
                      <div className="end-linha">
                        <span className="end-icone">✉</span>
                        <span>{endereco.email}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Formulário de edição inline */}
                {editando && (
                  <div className="cliente-card-edit">
                    <div className="form-grid-3">
                      <div className="field">
                        <label>Logradouro</label>
                        <input value={endEdit.logradouro} onChange={e => setEndEdit(p => ({ ...p, logradouro: e.target.value }))} />
                      </div>
                      <div className="field">
                        <label>Número</label>
                        <input value={endEdit.numero} onChange={e => setEndEdit(p => ({ ...p, numero: e.target.value }))} />
                      </div>
                      <div className="field">
                        <label>Bairro</label>
                        <input value={endEdit.bairro} onChange={e => setEndEdit(p => ({ ...p, bairro: e.target.value }))} />
                      </div>
                      <div className="field">
                        <label>Cidade</label>
                        <input value={endEdit.cidade} onChange={e => setEndEdit(p => ({ ...p, cidade: e.target.value }))} />
                      </div>
                      <div className="field">
                        <label>UF</label>
                        <input value={endEdit.uf} maxLength={2} onChange={e => setEndEdit(p => ({ ...p, uf: e.target.value }))} />
                      </div>
                      <div className="field">
                        <label>CEP</label>
                        <input value={endEdit.cep} onChange={e => setEndEdit(p => ({ ...p, cep: e.target.value }))} />
                      </div>
                      <div className="field">
                        <label>Telefone</label>
                        <input value={endEdit.telefone} onChange={e => setEndEdit(p => ({ ...p, telefone: e.target.value }))} />
                      </div>
                      <div className="field" style={{ gridColumn: 'span 2' }}>
                        <label>E-mail</label>
                        <input type="email" value={endEdit.email} onChange={e => setEndEdit(p => ({ ...p, email: e.target.value }))} />
                      </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.75rem' }}>
                      <button className="btn btn-primary btn-sm" onClick={salvarEdicao}>Confirmar</button>
                    </div>
                  </div>
                )}
                {/* Rodapé do card com todos os botões */}
                <div className="cliente-card-footer">
                  <button className="btn-editar" onClick={() => { setEditando(e => !e); setEndEdit(endereco ?? endEdit) }}>
                    {editando ? 'Cancelar' : 'Editar'}
                  </button>
                  {!editId && (
                    <button className="btn-trocar-cliente" onClick={() => { setClienteSelecionado(null); setBusca(''); setClientes([]); setEndereco(null); }}>
                      Trocar
                    </button>
                  )}
                  <span style={{ width: '1px', height: '18px', background: '#d1fae5', display: 'inline-block', margin: '0 0.25rem' }} />
                  <button className="btn btn-primary btn-sm" disabled={clienteSelecionado.bloqueado} onClick={() => setEtapa(1)}>
                    Próximo →
                  </button>
                </div>
              </div>
            )}

            {!clienteSelecionado && (
              <div className="etapa-acoes">
                <button className="btn btn-primary" disabled onClick={() => setEtapa(1)}>
                  Próximo →
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── Etapa 1: Dados ── */}
        {etapa === 1 && (
          <div>
            <h3 className="etapa-titulo" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              Dados do Documento
              {tipo === 'CTE' && <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#111827' }}>TNOT003</span>}
            </h3>

            <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', background: '#1a6ea8', borderRadius: '0.5rem', padding: '0.65rem 1rem', marginBottom: '0.85rem' }}>
              <div className="field" style={{ flex: '0 0 280px', marginBottom: 0 }}>
                <label style={{ color: '#bfdbfe', fontWeight: 700 }}>Empresa Emitente</label>
                <select
                  value={empresa?.empcod ?? ''}
                  onChange={e => setEmpresa(empresas.find(x => x.empcod === Number(e.target.value)) ?? null)}
                  style={{ fontWeight: 600 }}
                >
                  {empresas.map(emp => (
                    <option key={emp.empcod} value={emp.empcod}>
                      {emp.empcod} - {emp.empfan || emp.empnom}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field" style={{ flex: '0 0 160px', marginBottom: 0 }}>
                <label style={{ color: '#bfdbfe', fontWeight: 700 }}>Data de Emissão</label>
                <input type="date" value={dataEmissao} onChange={e => setDataEmissao(limitarAno4Digitos(dataEmissao, e.target.value))} style={{ fontWeight: 600 }} />
              </div>
              <div className="field" style={{ flex: '0 0 80px', marginBottom: 0 }}>
                <label style={{ color: '#bfdbfe', fontWeight: 700 }}>Série {carregandoNum && <span style={{ color: '#93c5fd', fontWeight: 400 }}>...</span>}</label>
                <input type="text" value={serie} onChange={e => setSerie(e.target.value)} maxLength={3} disabled={carregandoNum || !!editId} style={{ fontWeight: 600 }} />
              </div>
              <div className="field" style={{ flex: '0 0 120px', marginBottom: 0 }}>
                <label style={{ color: '#bfdbfe', fontWeight: 700 }}>Número {carregandoNum && <span style={{ color: '#93c5fd', fontWeight: 400 }}>...</span>}</label>
                <input type="number" value={numero} onChange={e => setNumero(e.target.value)} disabled={carregandoNum || !!editId} style={{ fontWeight: 600 }} />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <div className="field" style={{ flex: '0 0 280px' }}>
                <label>Finalidade {errosDados.includes('Finalidade') && <span style={{ color: '#dc2626' }}>*</span>}</label>
                <select value={finalidade} style={{ outline: errosDados.includes('Finalidade') ? '2px solid #dc2626' : 'none' }}
                  onChange={e => { setFinalidade(e.target.value); setErrosDados(p => p.filter(x => x !== 'Finalidade')) }}>
                  <option value="">Selecione</option>
                  {tipo === 'CTE' ? (
                    <>
                      <option value="CTO">CTO - CTe-OS Normal</option>
                      <option value="OUT">OUT - CTe-OS Outras Operações</option>
                    </>
                  ) : (
                    <>
                      <option value="1">1 - NF-e Normal</option>
                      <option value="2">2 - NF-e Complementar</option>
                      <option value="3">3 - NF-e de Ajuste</option>
                      <option value="4">4 - Devolução de Mercadoria</option>
                    </>
                  )}
                </select>
              </div>
              {finalidade === 'OUT' && (
                <div className="field" style={{ flex: '0 0 180px' }}>
                  <label>Tipo da Nota</label>
                  <select value={tipoNota} onChange={e => {
                    const v = e.target.value
                    setTipoNota(v)
                    if (v === '2' || v === '5') setOperacao('S')
                  }}>
                    <option value="1">1 - Normal</option>
                    <option value="2">2 - Complementar</option>
                    <option value="5">5 - Substituição</option>
                  </select>
                </div>
              )}
              {finalidade === 'OUT' && tipoNota === '1' && (
                <div className="field" style={{ flex: '0 0 130px' }}>
                  <label>Operação</label>
                  <select value={operacao} onChange={e => setOperacao(e.target.value)}>
                    <option value="S">Saída</option>
                    <option value="E">Entrada</option>
                  </select>
                </div>
              )}
              <div className="field" style={{ flex: '0 0 160px' }}>
                <label>Tipo Consumidor</label>
                <select value={tipoConsumidor} onChange={e => setTipoConsumidor(e.target.value)}>
                  <option value="0">0 - Normal</option>
                  <option value="1">1 - Consumidor Final</option>
                </select>
              </div>
              <div className="field" style={{ flex: '0 0 320px' }}>
                <label>Presença Consumidor {errosDados.includes('Presença Consumidor') && <span style={{ color: '#dc2626' }}>*</span>}</label>
                <select value={presencaConsumidor} style={{ outline: errosDados.includes('Presença Consumidor') ? '2px solid #dc2626' : 'none' }}
                  onChange={e => { setPresencaConsumidor(e.target.value); setErrosDados(p => p.filter(x => x !== 'Presença Consumidor')) }}>
                  <option value="">Selecione</option>
                  <option value="0">0 - Não se Aplica</option>
                  <option value="1">1 - Operação Presencial</option>
                  <option value="2">2 - Não Presencial (Internet)</option>
                  <option value="3">3 - Não Presencial (Teleatendimento)</option>
                  <option value="4">4 - Entrega a Domicílio</option>
                  <option value="9">9 - Outros</option>
                </select>
              </div>
              <div className="field" style={{ flex: '0 0 140px' }}>
                <label style={{ whiteSpace: 'nowrap' }}>Modelo Fiscal</label>
                <input type="text" value={modeloFiscal} readOnly style={{ background: '#f3f4f6', cursor: 'default' }} />
              </div>
              <div className="field" style={{ flex: '0 0 220px' }}>
                <label>Modal {errosDados.includes('Modal') && <span style={{ color: '#dc2626' }}>*</span>}</label>
                <select
                  value={modal}
                  style={{ outline: errosDados.includes('Modal') ? '2px solid #dc2626' : 'none' }}
                  onChange={e => { setModal(e.target.value); setNatureza(''); setErrosDados(p => p.filter(x => x !== 'Modal')) }}
                >
                  <option value="">Selecione</option>
                  {tipo !== 'CTE' && <option value="1">Municipal</option>}
                  <option value="2">Intermunicipal</option>
                  <option value="3">Interestadual</option>
                  <option value="4">Internacional</option>
                </select>
              </div>
            </div>

            <div style={{ marginBottom: '0.75rem' }}>
              <div className="field" style={{ maxWidth: '620px' }}>
                <label>Natureza de Operação {errosDados.includes('Natureza de Operação') && <span style={{ color: '#dc2626' }}>*</span>}</label>
                <div style={{ outline: errosDados.includes('Natureza de Operação') ? '2px solid #dc2626' : 'none', borderRadius: '0.45rem' }}>
                  <NaturezaSelect
                    naturezas={naturezas.filter(n => {
                      const natOk = finalidade === 'OUT' && operacao === 'E' ? n.tno_nat === '1' : n.tno_nat === '3'
                      const locMap: Record<string, string> = { '1': 'M', '2': 'U', '3': 'S', '4': 'I' }
                      const locOk = modal ? n.tno_loc === locMap[modal] : true
                      return natOk && locOk
                    })}
                    value={natureza}
                    onChange={v => { setNatureza(v); setErrosDados(p => p.filter(e => e !== 'Natureza de Operação')) }}
                  />
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <div className="field" style={{ flex: '0 0 430px' }}>
                <label>Chave CT-e</label>
                <input
                  type="text"
                  value={chaveNfe}
                  readOnly
                  style={{
                    background: '#eff6ff',
                    border: '1.5px solid #93c5fd',
                    color: '#0d2d4e',
                    fontWeight: 700,
                    fontFamily: 'monospace',
                    fontSize: '0.78rem',
                    letterSpacing: '0.04em',
                    cursor: 'default',
                  }}
                />
              </div>
            </div>

            <div className="etapa-acoes">
              <button className="btn btn-secondary" onClick={() => setEtapa(0)}>← Voltar</button>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.3rem' }}>
                {errosDados.length > 0 && (
                  <span style={{ fontSize: '0.75rem', color: '#dc2626', fontWeight: 600 }}>
                    Preencha: {errosDados.join(', ')}
                  </span>
                )}
                <button className="btn btn-primary" onClick={() => {
                  const erros: string[] = []
                  if (!natureza) erros.push('Natureza de Operação')
                  if (!finalidade) erros.push('Finalidade')
                  if (!presencaConsumidor) erros.push('Presença Consumidor')
                  if (!numero) erros.push('Número')
                  if (tipo === 'CTE' && !modal) erros.push('Modal')
                  if (modal && natureza) {
                    const locMap: Record<string, string> = { '1': 'M', '2': 'U', '3': 'S', '4': 'I' }
                    const nat = naturezas.find(n => String(n.tno_cod) === natureza)
                    if (nat && nat.tno_loc && nat.tno_loc !== locMap[modal]) {
                      erros.push('CFOP incompatível com o Modal selecionado')
                    }
                  }
                  setErrosDados(erros)
                  if (erros.length === 0) setEtapa(2)
                }}>Próximo →</button>
              </div>
            </div>
          </div>
        )}

        {/* ── Etapa 2: Itens ── */}
        {etapa === 2 && (
          <div>
            <h3 className="etapa-titulo" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              Itens / Serviços
              {tipo === 'CTE' && <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#111827' }}>TNOT002</span>}
            </h3>
            <table>
              <thead>
                <tr>
                  <th>Item</th>
                  <th style={{ width: '8%' }}>Qtd</th>
                  <th style={{ width: '16%' }}>Vl. Unit.</th>
                  <th style={{ width: '13%' }}>Total</th>
                  <th style={{ width: '8%' }}></th>
                </tr>
              </thead>
              <tbody>
                {itens.map((it, i) => (
                  <React.Fragment key={i}>
                    <tr>
                      <td style={{ padding: '0.35rem 0.5rem' }}>
                        <ItemSelect somenteAtivos
                          codValue={it.itsCod}
                          descValue={it.descricao}
                          unValue={it.unidade}
                          onChange={(cod, des, un) => {
                            setItens(prev => prev.map((x, idx) =>
                              idx === i ? { ...x, itsCod: cod, descricao: des, unidade: un } : x
                            ))
                            if (des && it.quantidade > 0 && it.valorUnitario > 0) {
                              setItemExpandido(i)
                            }
                          }}
                        />
                      </td>
                      <td><input type="number" min={0} value={it.quantidade} onChange={e => updateItem(i, 'quantidade', Number(e.target.value))} /></td>
                      <td><input type="number" min={0} step={0.01} value={it.valorUnitario} onChange={e => {
                        const raw = e.target.value.replace(/^0+(?=\d)/, '')
                        e.target.value = raw
                        updateItem(i, 'valorUnitario', Number(raw) || 0)
                      }} /></td>
                      <td style={{ fontWeight: 600 }}>{(it.quantidade * it.valorUnitario).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</td>
                      <td>
                        <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'center' }}>
                          <button
                            className="btn btn-secondary btn-sm"
                            title="Dados fiscais"
                            onClick={() => setItemExpandido(itemExpandido === i ? null : i)}
                          >
                            {itemExpandido === i ? '▴' : '▾'}
                          </button>
                          {itens.length > 1 && (
                            <button className="btn btn-danger btn-sm" onClick={() => removeItem(i)}>✕</button>
                          )}
                        </div>
                      </td>
                    </tr>

                    {/* Painel de dados fiscais */}
                    {itemExpandido === i && (
                      <tr>
                        <td colSpan={5} style={{ background: '#f8fafc', padding: '1rem', borderTop: '1px dashed #e5e7eb' }}>
                          <div className="item-fiscal-grid">

                            <div className="field">
                              <label>Complemento da Descrição</label>
                              <input value={it.complemento} onChange={e => updateItem(i, 'complemento', e.target.value)} placeholder="Informação adicional do item" />
                            </div>

                            <div className="field">
                              <label>IBS/CBS (Regra)</label>
                              <IbsCbsSelect
                                codValue={it.ibsCbsRegra}
                                desValue={it.ibsCbsDes}
                                docFiscalPermitido="67"
                                onChange={async (cod, des) => {
                                  const total = it.quantidade * it.valorUnitario
                                  const baseIbsCbs = Math.round((total - (it.impostos.icmsDest || 0) - (it.impostos.pisDest || 0) - (it.impostos.cofinsDest || 0)) * 100) / 100
                                  let ibsCbs = { ...ibsCbsVazio }
                                  let munIbge = ''
                                  let munPatch: Partial<Item> = {}
                                  if (cod) {
                                    try {
                                      const r = await api.get(`/erp/ibscbs/${cod}`)
                                      if (r.data) {
                                        ibsCbs = calcIbsCbs(baseIbsCbs, r.data)
                                        if (r.data.mun_cod) {
                                          munIbge = String(r.data.mun_ibge ?? '')
                                          // Regra é vinculada a um município — IBS/CBS é devido na cidade de início da viagem.
                                          // Só preenche se ainda estiver vazio; se o usuário já escolheu uma cidade, não sobrescreve
                                          // (a validação ao avançar avisa se ficar incoerente com a regra).
                                          if (!it.cidOrigemCod) {
                                            munPatch = { cidOrigemCod: String(r.data.mun_cod), cidOrigemDes: r.data.mun_des, cidOrigemIbge: munIbge, cidOrigemUf: r.data.mun_uf }
                                          }
                                        }
                                      }
                                    } catch {}
                                  }
                                  setItens(prev => prev.map((x, idx) =>
                                    idx === i ? { ...x, ibsCbsRegra: cod, ibsCbsDes: des, ibsCbsMunIbge: munIbge, ibsCbs, ...munPatch } : x
                                  ))
                                }}
                              />
                            </div>

                            <div className="field">
                              <label>Cidade Origem</label>
                              <CidadeSelect
                                codValue={it.cidOrigemCod}
                                desValue={it.cidOrigemDes}
                                ibgeValue={it.cidOrigemIbge}
                                ufValue={it.cidOrigemUf}
                                filtroUf={tipo === 'CTE' && modal === '2' ? it.cidDestinoUf : undefined}
                                onChange={(cod, des, ibge, uf) => setItens(prev => prev.map((x, idx) =>
                                  idx === i ? { ...x, cidOrigemCod: cod, cidOrigemDes: des, cidOrigemIbge: ibge, cidOrigemUf: uf } : x
                                ))}
                              />
                            </div>

                            <div className="field">
                              <label>Cidade Destino</label>
                              <CidadeSelect
                                codValue={it.cidDestinoCod}
                                desValue={it.cidDestinoDes}
                                ibgeValue={it.cidDestinoIbge}
                                ufValue={it.cidDestinoUf}
                                filtroUf={tipo === 'CTE' && modal === '2' ? it.cidOrigemUf : undefined}
                                onChange={(cod, des, ibge, uf) => setItens(prev => prev.map((x, idx) =>
                                  idx === i ? { ...x, cidDestinoCod: cod, cidDestinoDes: des, cidDestinoIbge: ibge, cidDestinoUf: uf } : x
                                ))}
                              />
                            </div>

                            {/* Abas fiscais */}
                            <div className="field item-fiscal-span2" style={{ marginTop: '0.5rem' }}>
                              <div className="fiscal-tabs">
                                <button type="button" className={`fiscal-tab${(abaFiscal[i] ?? 'impostos') === 'impostos' ? ' fiscal-tab--ativa' : ''}`} onClick={() => setAbaFiscal(prev => ({ ...prev, [i]: 'impostos' }))}>Impostos</button>
                                <button type="button" className={`fiscal-tab${(abaFiscal[i] ?? 'impostos') === 'ibscbs' ? ' fiscal-tab--ativa' : ''}`} onClick={() => setAbaFiscal(prev => ({ ...prev, [i]: 'ibscbs' }))}>IBS/CBS/IS</button>
                              </div>
                              {(abaFiscal[i] ?? 'impostos') === 'impostos' && (
                              <div>
                              <table className="imp-table">
                                <thead>
                                  <tr>
                                    <th style={{ width: '60px' }}>Tributo</th>
                                    <th style={{ width: '125px' }}>Base Cálculo</th>
                                    <th style={{ width: '75px' }}>Alíquota</th>
                                    <th style={{ width: '125px' }}>Destaque</th>
                                    <th style={{ width: '95px' }}>Retenção</th>
                                    <th style={{ width: '60px' }}>CST</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {[
                                    { label: 'ICMS',    bc: 'icmsBase',   aliq: 'icmsAliq',   dest: 'icmsDest',   ret: null,        cst: 'icmsCst'   },
                                    { label: 'ICMS ST', bc: 'stBase',     aliq: 'stAliq',     dest: 'stDest',     ret: null,        cst: null        },
                                    { label: 'IPI',     bc: 'ipiBase',    aliq: 'ipiAliq',    dest: 'ipiDest',    ret: null,        cst: 'ipiCst'    },
                                    { label: 'ISSQN',   bc: 'issqnBase',  aliq: 'issqnAliq',  dest: 'issqnDest',  ret: 'issqnRet',  cst: null        },
                                    { label: 'INSS',    bc: 'inssBase',   aliq: 'inssAliq',   dest: 'inssDest',   ret: 'inssRet',   cst: null        },
                                    { label: 'IRRF',    bc: 'irrfBase',   aliq: 'irrfAliq',   dest: 'irrfDest',   ret: 'irrfRet',   cst: null        },
                                    { label: 'PIS',     bc: 'pisBase',    aliq: 'pisAliq',    dest: 'pisDest',    ret: 'pisRet',    cst: 'pisCst'    },
                                    { label: 'COFINS',  bc: 'cofinsBase', aliq: 'cofinsAliq', dest: 'cofinsDest', ret: 'cofinsRet', cst: 'cofinsCst' },
                                    { label: 'CSLL',    bc: 'csllBase',   aliq: 'csllAliq',   dest: 'csllDest',   ret: 'csllRet',   cst: null        },
                                  ].map(row => (
                                    <tr key={row.label}>
                                      <td className="imp-label">{row.label}</td>
                                      <td><input type="text" className="imp-input" min={0} step={0.01}
                                        value={f2((it.impostos as any)[row.bc])}
                                        onChange={e => setItens(prev => prev.map((x, idx) => idx === i ? { ...x, impostos: { ...x.impostos, [row.bc]: Number(e.target.value) } } : x))} /></td>
                                      <td><input type="text" className="imp-input imp-aliq" min={0} step={0.001}
                                        value={f2((it.impostos as any)[row.aliq])}
                                        onChange={e => setItens(prev => prev.map((x, idx) => idx === i ? { ...x, impostos: { ...x.impostos, [row.aliq]: Number(e.target.value) } } : x))} /></td>
                                      <td><input type="text" className="imp-input" min={0} step={0.01}
                                        value={f2((it.impostos as any)[row.dest])}
                                        onChange={e => setItens(prev => prev.map((x, idx) => idx === i ? { ...x, impostos: { ...x.impostos, [row.dest]: Number(e.target.value) } } : x))} /></td>
                                      <td>{row.ret
                                        ? <input type="text" className="imp-input" min={0} step={0.01}
                                            value={f2((it.impostos as any)[row.ret])}
                                            onChange={e => setItens(prev => prev.map((x, idx) => idx === i ? { ...x, impostos: { ...x.impostos, [row.ret!]: Number(e.target.value) } } : x))} />
                                        : <span className="imp-vazio">—</span>}
                                      </td>
                                      <td>{row.cst
                                        ? <input type="text" className="imp-input imp-cst"
                                            value={(it.impostos as any)[row.cst]}
                                            onChange={e => setItens(prev => prev.map((x, idx) => idx === i ? { ...x, impostos: { ...x.impostos, [row.cst!]: e.target.value } } : x))} />
                                        : <span className="imp-vazio">—</span>}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                              </div>
                              )}
                              {(abaFiscal[i] ?? 'impostos') === 'ibscbs' && (
                              <div>
                                <table className="imp-table" style={{ marginTop: '0.35rem' }}>
                                  <thead>
                                    <tr>
                                      <th style={{ width: '60px' }}>Tributo</th>
                                      <th style={{ width: '55px' }}>CST</th>
                                      <th style={{ width: '110px' }}>Base Cálculo</th>
                                      <th style={{ width: '115px' }}>BC Efetiva</th>
                                      <th style={{ width: '75px' }}>Ali. Ref %</th>
                                      <th style={{ width: '75px' }}>Red. Ali %</th>
                                      <th style={{ width: '75px' }}>Ali. Efet %</th>
                                      <th style={{ width: '110px' }}>Valor</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr>
                                      <td className="imp-label">CBS</td>
                                      <td style={{ textAlign: 'center', fontSize: '0.78rem' }}>{it.ibsCbs.cst}</td>
                                      <td><input className="imp-input" type="text" readOnly value={f2(it.ibsCbs.cbsBc)} /></td>
                                      <td><input className="imp-input" type="text" readOnly value={f2(it.ibsCbs.cbsBcEf)} /></td>
                                      <td><input className="imp-input" type="text" readOnly value={f2(it.ibsCbs.cbsAliRef)} /></td>
                                      <td><input className="imp-input" type="text" readOnly value={f2(it.ibsCbs.cbsRedAli)} /></td>
                                      <td><input className="imp-input" type="text" readOnly value={f2(it.ibsCbs.cbsAliEf)} /></td>
                                      <td><input className="imp-input" type="text" readOnly value={f2(it.ibsCbs.cbsValor)} style={{ fontWeight: 800, color: '#1a6ea8' }} /></td>
                                    </tr>
                                    <tr>
                                      <td className="imp-label">IBS UF</td>
                                      <td style={{ textAlign: 'center', fontSize: '0.78rem', color: '#6b7280' }}>{it.ibsCbs.ibsUfEst}</td>
                                      <td><input className="imp-input" type="text" readOnly value={f2(it.ibsCbs.cbsBc)} /></td>
                                      <td><input className="imp-input" type="text" readOnly value={f2(it.ibsCbs.ibsUfBcEf)} /></td>
                                      <td><input className="imp-input" type="text" readOnly value={f2(it.ibsCbs.ibsUfAliRef)} /></td>
                                      <td><input className="imp-input" type="text" readOnly value={f2(it.ibsCbs.ibsUfRedAli)} /></td>
                                      <td><input className="imp-input" type="text" readOnly value={f2(it.ibsCbs.ibsUfAliEf)} /></td>
                                      <td><input className="imp-input" type="text" readOnly value={f2(it.ibsCbs.ibsUfValor)} style={{ fontWeight: 800, color: '#1a6ea8' }} /></td>
                                    </tr>
                                    <tr>
                                      <td className="imp-label">IBS MUN</td>
                                      <td></td>
                                      <td><input className="imp-input" type="text" readOnly value={f2(it.ibsCbs.cbsBc)} /></td>
                                      <td><input className="imp-input" type="text" readOnly value={f2(it.ibsCbs.ibsMunBcEf)} /></td>
                                      <td><input className="imp-input" type="text" readOnly value={f2(it.ibsCbs.ibsMunAliRef)} /></td>
                                      <td><input className="imp-input" type="text" readOnly value={f2(it.ibsCbs.ibsMunRedAli)} /></td>
                                      <td><input className="imp-input" type="text" readOnly value={f2(it.ibsCbs.ibsMunAliEf)} /></td>
                                      <td><input className="imp-input" type="text" readOnly value={f2(it.ibsCbs.ibsMunValor)} style={{ fontWeight: 800, color: '#1a6ea8' }} /></td>
                                    </tr>
                                    <tr>
                                      <td className="imp-label">IS</td>
                                      <td></td>
                                      <td><input className="imp-input" type="text" readOnly value={f2(it.ibsCbs.isSbas)} /></td>
                                      <td colSpan={3}></td>
                                      <td><input className="imp-input" type="text" readOnly value={f2(it.ibsCbs.isSali)} /></td>
                                      <td></td>
                                    </tr>
                                  </tbody>
                                </table>
                                {it.ibsCbs.classTrib && (
                                  <div style={{ marginTop: '0.35rem', fontSize: '0.75rem', color: '#6b7280' }}>
                                    <strong>Class. Trib.:</strong> {it.ibsCbs.classTrib} — {it.ibsCbs.classTribDes}
                                  </div>
                                )}
                              </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '1rem', maxWidth: '1000px' }}>
              {tipo !== 'CTE' && <button className="btn btn-secondary" onClick={addItem}>+ Adicionar Item</button>}
              <strong style={{ color: '#1a6ea8', fontSize: '0.95rem' }}>
                Total: {totalItens.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
              </strong>
            </div>
            <div className="etapa-acoes">
              <button className="btn btn-secondary" onClick={() => setEtapa(1)}>← Voltar</button>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '0.3rem' }}>
                {tentouAvancarItens && (() => {
                  const faltando: string[] = []
                  itens.forEach((it, i) => {
                    const n = `Item ${i + 1}`
                    if (!it.descricao)     faltando.push(`${n}: Item`)
                    if (!it.quantidade)    faltando.push(`${n}: QTD`)
                    if (!it.valorUnitario) faltando.push(`${n}: Vl. Unit.`)
                    if (!it.ibsCbsRegra)   faltando.push(`${n}: IBS/CBS`)
                    if (!it.cidOrigemCod)  faltando.push(`${n}: Cidade Origem`)
                    if (!it.cidDestinoCod) faltando.push(`${n}: Cidade Destino`)
                    if (it.ibsCbsMunIbge && it.ibsCbsMunIbge !== it.cidOrigemIbge)
                      faltando.push(`${n}: regra IBS/CBS "${it.ibsCbsDes}" exige Cidade Origem diferente da informada`)
                  })
                  return faltando.length > 0 ? (
                    <span style={{ fontSize: '0.75rem', color: '#dc2626', fontWeight: 600, maxWidth: '500px' }}>
                      Preencha: {faltando.join(' · ')}
                    </span>
                  ) : null
                })()}
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    const temErro = itens.some(it =>
                      !it.descricao || !it.quantidade || !it.valorUnitario ||
                      !it.ibsCbsRegra ||
                      !it.cidOrigemCod || !it.cidDestinoCod ||
                      (!!it.ibsCbsMunIbge && it.ibsCbsMunIbge !== it.cidOrigemIbge)
                    )
                    setTentouAvancarItens(true)
                    if (!temErro) setEtapa(3)
                  }}
                >Próximo →</button>
              </div>
            </div>
          </div>
        )}

        {/* ── Etapa 3: Viagem ── */}
        {etapa === 3 && (
          <div>
            <h3 className="etapa-titulo" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              Dados da Viagem
              {tipo === 'CTE' && <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#111827' }}>TNTVCPO</span>}
            </h3>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <div className="field" style={{ flex: '0 0 200px' }}>
                <label>Tipo de Viagem</label>
                <select value={tipoViagem} onChange={e => setTipoViagem(e.target.value)}>
                  <option value="0">Ida e Volta</option>
                  <option value="1">Somente Ida</option>
                </select>
              </div>
              {tipo === 'CTE' && (
                <div className="field" style={{ flex: '0 0 200px' }}>
                  <label>Nº da Viagem</label>
                  <div style={{ display: 'flex', gap: '0.3rem' }}>
                    <input
                      type="text"
                      inputMode="numeric"
                      placeholder="Opcional"
                      value={numeroViagem}
                      onChange={e => setNumeroViagem(e.target.value.replace(/\D/g, ''))}
                      style={{ fontWeight: 700, background: '#fffbeb', border: '2px solid #f59e0b', color: '#92400e' }}
                    />
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      title="Buscar fichas de viagem do cliente"
                      disabled={!clienteSelecionado}
                      onClick={() => setModalFichaViagem(true)}
                      style={{ borderColor: '#f59e0b' }}
                    >🔍</button>
                  </div>
                </div>
              )}
              {modalFichaViagem && clienteSelecionado && (
                <FichaViagemModal
                  cnpj={clienteSelecionado.cli_mat}
                  onClose={() => setModalFichaViagem(false)}
                  onSelect={ficcod => { setNumeroViagem(String(ficcod)); setModalFichaViagem(false) }}
                />
              )}
              <div className="field" style={{ flex: '0 0 220px' }}>
                <label>Tipo de Serviço</label>
                <select
                  value={tipoServico}
                  disabled={!!numeroViagem}
                  onChange={e => {
                    setTipoServico(e.target.value)
                    if (e.target.value === '7') setDhViagem('')
                  }}
                >
                  <option value="6">Eventual</option>
                  <option value="7">Contínuo</option>
                </select>
                {!!numeroViagem && (
                  <span style={{ fontSize: '0.72rem', color: '#92400e' }}>Fixado em Eventual — nota vinculada a uma ficha de viagem</span>
                )}
              </div>
              {tipoServico !== '7' && (
                <div className="field" style={{ flex: '0 0 200px' }}>
                  <label>
                    Data/Hora da Viagem
                    {tipoServico === '6' && <span style={{ color: '#dc2626', marginLeft: '0.2rem' }}>*</span>}
                  </label>
                  <input
                    type="datetime-local"
                    value={dhViagem}
                    min={nowLocalDateTimeInput()}
                    onChange={e => setDhViagem(limitarAno4Digitos(dhViagem, e.target.value))}
                    style={tentouAvancarViagem && ((tipoServico === '6' && !dhViagem) || (dhViagem && dhViagem < nowLocalDateTimeInput())) ? { borderColor: '#dc2626' } : undefined}
                  />
                  {tentouAvancarViagem && tipoServico === '6' && !dhViagem && (
                    <span style={{ fontSize: '0.72rem', color: '#dc2626' }}>Obrigatório para serviço Eventual</span>
                  )}
                  {tentouAvancarViagem && dhViagem && dhViagem < nowLocalDateTimeInput() && (
                    <span style={{ fontSize: '0.72rem', color: '#dc2626' }}>Não pode ser uma data/hora passada — a SEFAZ rejeita o CT-e</span>
                  )}
                </div>
              )}
              <div className="field" style={{ flex: '0 0 auto' }}>
                <label>Veículo</label>
                <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'center' }}>
                  <input
                    type="number"
                    min={1}
                    value={veiCod}
                    style={{ width: 80 }}
                    onChange={e => {
                      setVeiCod(e.target.value)
                      if (!e.target.value) {
                        setVeiDados(null)
                        setVeiPlaca('')
                        setVeiRenavan('')
                        setVeiUf('')
                        setQtdPassageiros('')
                      }
                    }}
                    onBlur={async e => {
                      const cod = e.target.value.trim()
                      if (!cod) return
                      try {
                        const r = await api.get(`/erp/veiculos/${cod}`)
                        const dados = r.data
                        if (dados) {
                          setVeiDados(dados)
                          setVeiPlaca(dados.veipla || '')
                          setVeiRenavan(dados.veiren || '')
                          setVeiUf(dados.veiestado || '')
                          setQtdPassageiros(String(dados.veinpa || ''))
                        } else {
                          setVeiDados(null)
                          setVeiPlaca('')
                          setVeiRenavan('')
                          setVeiUf('')
                          setQtdPassageiros('')
                        }
                      } catch { /* ignora */ }
                    }}
                    placeholder="Cód."
                  />
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    title="Consultar veículos"
                    onClick={() => setModalVeiculo(true)}
                  >🔍</button>
                </div>
              </div>
              {veiDados && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '1rem',
                  background: '#f0f4ff', border: '1.5px solid #b0c4ef',
                  borderRadius: '0.5rem', padding: '0.35rem 0.85rem',
                  fontSize: '0.82rem', color: '#0d2d4e', marginTop: '1.35rem',
                  flexWrap: 'wrap'
                }}>
                  <span><strong>Placa:</strong> {veiDados.veipla}</span>
                  <span><strong>Renavan:</strong> {veiDados.veiren}</span>
                  <span><strong>UF:</strong> {veiDados.veiestado}</span>
                  <span><strong>Passageiros:</strong> {veiDados.veinpa}</span>
                </div>
              )}
              {modalVeiculo && (
                <VeiculoModal
                  onClose={() => setModalVeiculo(false)}
                  onSelect={async v => {
                    setModalVeiculo(false)
                    setVeiCod(v.veicod)
                    try {
                      const r = await api.get(`/erp/veiculos/${v.veicod}`)
                      const dados = r.data
                      if (dados) {
                        setVeiDados(dados)
                        setVeiPlaca(dados.veipla || '')
                        setVeiRenavan(dados.veiren || '')
                        setVeiUf(dados.veiestado || '')
                        setQtdPassageiros(String(dados.veinpa || ''))
                      }
                    } catch { /* ignora */ }
                  }}
                />
              )}
            </div>
            {(() => {
              const ufs = new Set(itens.flatMap(it => [it.cidOrigemUf, it.cidDestinoUf].filter(Boolean)))
              const multiEstado = ufs.size > 1
              return (
                <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginTop: '0.25rem' }}>
                  <div className="field" style={{ flex: '0 0 480px' }}>
                    <label>Descrição do Serviço Prestado</label>
                    <textarea
                      rows={5}
                      value={descServico}
                      maxLength={160}
                      onChange={e => setDescServico(e.target.value.slice(0, 160))}
                      placeholder="Máximo 160 caracteres"
                      style={{ resize: 'vertical', fontFamily: 'inherit', fontSize: '0.85rem', padding: '0.4rem 0.5rem', border: descServico.length >= 160 ? '1.5px solid #dc2626' : '1.5px solid #d1d5db', borderRadius: '0.4rem', width: '100%', boxSizing: 'border-box' }}
                    />
                    <div style={{ fontSize: '0.72rem', textAlign: 'right', marginTop: '0.15rem', color: descServico.length >= 160 ? '#dc2626' : '#9ca3af' }}>
                      {descServico.length >= 160 ? 'Limite atingido!' : `${descServico.length}/160`}
                    </div>
                  </div>
                  {multiEstado && (
                    <div className="field" style={{ flex: '0 0 220px' }}>
                      <label>Estados que Compõem a Viagem</label>
                      <textarea rows={2} value={estadosViagem} onChange={e => setEstadosViagem(e.target.value)} placeholder="Ex: RS, SC, PR" style={{ resize: 'vertical', fontFamily: 'inherit', fontSize: '0.85rem', padding: '0.4rem 0.5rem', border: '1.5px solid #d1d5db', borderRadius: '0.4rem', width: '100%', boxSizing: 'border-box' }} />
                    </div>
                  )}
                </div>
              )
            })()}
            {tipo === 'CTE' && (
              <div style={{ border: `1.5px solid ${compraGov.ativo ? '#1b3f7b' : '#e5e7eb'}`, background: compraGov.ativo ? '#eef3f9' : '#fff', borderRadius: 8, padding: '0.6rem 0.8rem', margin: '0.25rem 0 0.6rem', maxWidth: 820 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: '0.85rem', color: '#1b3f7b', cursor: 'pointer' }}>
                  <input type="checkbox" checked={compraGov.ativo}
                    onChange={e => setCompraGov(p => ({ ...p, ativo: e.target.checked, tpEnteGov: p.tpEnteGov || '4' }))} />
                  Compra governamental (tomador é ente público)
                  {compraGov.doCadastro && <span style={{ fontWeight: 500, fontSize: '0.72rem', color: '#6b7280' }}>— marcado pelo cadastro do cliente</span>}
                </label>
                {compraGov.ativo && (
                  <div style={{ display: 'flex', gap: '0.65rem', flexWrap: 'wrap', marginTop: '0.55rem', alignItems: 'flex-end' }}>
                    <div className="field" style={{ flex: '0 0 190px', marginBottom: 0 }}>
                      <label>Tipo de Ente</label>
                      <select value={compraGov.tpEnteGov} onChange={e => setCompraGov(p => ({ ...p, tpEnteGov: e.target.value }))}>
                        {Object.entries(TP_ENTE_GOV).map(([k, v]) => <option key={k} value={k}>{k} - {v}</option>)}
                      </select>
                    </div>
                    <div className="field" style={{ flex: '0 0 110px', marginBottom: 0 }}>
                      <label>Redutor %</label>
                      <input value={compraGov.pRedutor} inputMode="decimal" title="Percentual de redução do art. 472 da LC 214 (em 2026 a redução ainda não vale — deixe 0)"
                        onChange={e => setCompraGov(p => ({ ...p, pRedutor: e.target.value.replace(/[^\d,.]/g, '') }))} />
                    </div>
                    <div className="field" style={{ flex: '1 1 320px', marginBottom: 0 }}>
                      <label>Tipo da Operação</label>
                      <select value={compraGov.tpOperGov} onChange={e => setCompraGov(p => ({ ...p, tpOperGov: e.target.value, refDFeAnt: ['2', '3'].includes(e.target.value) ? p.refDFeAnt : '' }))}>
                        {Object.entries(TP_OPER_GOV).map(([k, v]) => <option key={k} value={k}>{k} - {v}</option>)}
                      </select>
                    </div>
                    {['2', '3'].includes(compraGov.tpOperGov) && (
                      <div className="field" style={{ flex: '1 1 100%', marginBottom: 0 }}>
                        <label>Chave do documento anterior {compraGov.tpOperGov === '2' ? '(exatamente uma)' : '(uma ou mais, separadas por vírgula)'}</label>
                        <input value={compraGov.refDFeAnt} placeholder="44 dígitos"
                          onChange={e => setCompraGov(p => ({ ...p, refDFeAnt: e.target.value.replace(/[^\d,;\s]/g, '') }))} />
                      </div>
                    )}
                    <div style={{ flex: '1 1 100%', fontSize: '0.72rem', color: '#6b7280' }}>
                      O XML leva o grupo de compra governamental e o IBS/CBS sai com o redutor. Em 2026 a redução da LC 214 ainda não vale (use 0);
                      a partir de 2027 o IBS/CBS dos outros entes é zerado e o ente comprador fica com a soma.
                    </div>
                  </div>
                )}
              </div>
            )}
            {tipo === 'CTE' && (
              <div className="field" style={{ marginTop: '0.25rem' }}>
                <label>Carac. Adicionais</label>
                <input
                  value={caracAd}
                  maxLength={30}
                  onChange={e => setCaracAd(e.target.value.slice(0, 30))}
                  placeholder="Ex: FRS 1016431623"
                  title="Vai no XML como xCaracSer — alguns clientes (ex.: Braskem) exigem o FRS/pedido aqui"
                  style={{ width: '32ch', maxWidth: '100%' }}
                />
              </div>
            )}
            <div className="field" style={{ maxWidth: 480, marginTop: '0.25rem' }}>
              <label>Informações Complementares</label>
              <textarea
                value={obs}
                onChange={e => setObs(e.target.value)}
                rows={3}
                style={{ resize: 'vertical', fontFamily: 'inherit', fontSize: '0.85rem', padding: '0.4rem 0.5rem', border: '1.5px solid #eab308', background: '#fef9c3', borderRadius: '0.4rem', width: '100%', boxSizing: 'border-box' }}
              />
            </div>
            <div className="etapa-acoes">
              <button className="btn btn-secondary" onClick={() => setEtapa(2)}>← Voltar</button>
              <button className="btn btn-primary" onClick={() => {
                setTentouAvancarViagem(true)
                if (tipoServico === '6' && !dhViagem) return
                if (dhViagem && dhViagem < nowLocalDateTimeInput()) return
                setEtapa(4)
              }}>Próximo →</button>
            </div>
          </div>
        )}

        {/* ── Etapa 4: Revisão ── */}
        {etapa === 4 && (() => {
          const brl = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
          const pct = (v: number) => `${f2(v)}%`
          const totalIcms    = itens.reduce((s, it) => s + (it.impostos.icmsDest    || 0), 0)
          const totalIssqn   = itens.reduce((s, it) => s + (it.impostos.issqnDest   || 0), 0)
          const totalInss    = itens.reduce((s, it) => s + (it.impostos.inssDest    || 0), 0)
          const totalIrrf    = itens.reduce((s, it) => s + (it.impostos.irrfDest    || 0), 0)
          const totalPis     = itens.reduce((s, it) => s + (it.impostos.pisDest     || 0), 0)
          const totalCofins  = itens.reduce((s, it) => s + (it.impostos.cofinsDest  || 0), 0)
          const totalCsll    = itens.reduce((s, it) => s + (it.impostos.csllDest    || 0), 0)
          const totalCbs     = itens.reduce((s, it) => s + (it.ibsCbs.cbsValor      || 0), 0)
          const totalIbsUf   = itens.reduce((s, it) => s + (it.ibsCbs.ibsUfValor   || 0), 0)
          const totalIbsMun  = itens.reduce((s, it) => s + (it.ibsCbs.ibsMunValor  || 0), 0)
          // Retenções de verdade (deduzidas do valor pago pelo cliente) — INSS/IRRF/PIS/COFINS
          // só contam se a natureza realmente retém (flag tno_rin/tno_rir/tno_rip); CSLL
          // nunca aparece "não retida" (não existe esse caso, por isso sempre soma no líquido)
          const inssRetido   = itens.some(it => (it.impostos.inssRet   || 0) > 0)
          const irrfRetido   = itens.some(it => (it.impostos.irrfRet   || 0) > 0)
          const pisRetido    = itens.some(it => (it.impostos.pisRet    || 0) > 0)
          const cofinsRetido = itens.some(it => (it.impostos.cofinsRet || 0) > 0)
          const totalRetencoes = (inssRetido ? totalInss : 0) + (irrfRetido ? totalIrrf : 0) + (pisRetido ? totalPis : 0) + (cofinsRetido ? totalCofins : 0) + totalCsll
          const totalLiquido   = totalItens - totalRetencoes
          const impostosList = [
            { label: 'ICMS',                                     valor: totalIcms,   retido: false },
            { label: 'ISSQN',                                    valor: totalIssqn,  retido: false },
            { label: inssRetido   ? 'INSS Retido'   : 'INSS',     valor: totalInss,   retido: inssRetido },
            { label: irrfRetido   ? 'IRRF Retido'   : 'IRRF',     valor: totalIrrf,   retido: irrfRetido },
            { label: pisRetido    ? 'PIS Retido'    : 'PIS',      valor: totalPis,    retido: pisRetido },
            { label: cofinsRetido ? 'COFINS Retido' : 'COFINS',  valor: totalCofins, retido: cofinsRetido },
            { label: 'CSLL Retido',                              valor: totalCsll,   retido: true  },
          ].filter(x => x.valor > 0)
          const ibscbsList = [
            { label: 'CBS',     valor: totalCbs    },
            { label: 'IBS UF',  valor: totalIbsUf  },
            { label: 'IBS MUN', valor: totalIbsMun },
          ].filter(x => x.valor > 0)
          const secStyle: React.CSSProperties = { marginBottom: '1rem', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '0.5rem', padding: '0.75rem 1rem' }
          const labelStyle: React.CSSProperties = { fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6b7280', marginBottom: '0.4rem' }
          const rowStyle: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', padding: '0.18rem 0', borderBottom: '1px dashed #e5e7eb' }
          const tagStyle = (color: string): React.CSSProperties => ({ display: 'inline-block', fontSize: '0.7rem', fontWeight: 700, background: color, borderRadius: '0.25rem', padding: '0.1rem 0.4rem', marginRight: '0.3rem' })
          return (
          <div>
            <h3 className="etapa-titulo" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              Revisão e Emissão
              {tipo === 'CTE' && <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#111827' }}>WPREVCTEO</span>}
            </h3>

            {/* Cabeçalho */}
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
              <div style={{ ...secStyle, flex: '1 1 220px' }}>
                <div style={labelStyle}>Cliente</div>
                <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>{clienteSelecionado?.cli_nom}</div>
                <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>{clienteSelecionado ? formatarDocumento(clienteSelecionado.cli_mat) : ''}</div>
                {endereco?.logradouro?.trim() && <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{endereco.logradouro}, {endereco.numero} — {endereco.cidade}/{endereco.uf}</div>}
                {endereco?.telefone?.trim() && <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Tel: {endereco.telefone}</div>}
                {endereco?.email?.trim() && <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{endereco.email}</div>}
              </div>
              <div style={{ ...secStyle, flex: '1 1 180px' }}>
                <div style={labelStyle}>Documento</div>
                <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>{titulo} · Série {serie} · Nº {numero}</div>
                <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>Emissão: {dataEmissao}</div>
                <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>CFOP: {nat?.tno_cdf} — {nat?.tno_des}</div>
              </div>
              <div style={{ ...secStyle, flex: '0 0 auto', textAlign: 'center' }}>
                <div style={labelStyle}>Total da Nota</div>
                <div style={{ fontWeight: 800, fontSize: '1.4rem', color: '#f07820' }}>{brl(totalItens)}</div>
                <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>{itens.length} item(s)</div>
                {totalRetencoes > 0 && (
                  <>
                    <div style={{ fontSize: '0.78rem', color: '#dc2626', marginTop: 4 }}>(-) Retenções: {brl(totalRetencoes)}</div>
                    <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#15803d', marginTop: 4, borderTop: '1px solid #e5e7eb', paddingTop: 4 }}>Líquido: {brl(totalLiquido)}</div>
                  </>
                )}
              </div>
            </div>

            {/* Viagem */}
            <div style={secStyle}>
              <div style={labelStyle}>Viagem</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', fontSize: '0.82rem' }}>
                <span><strong>Tipo de Serviço:</strong> {tipoServico === '6' ? 'Eventual' : 'Contínuo'}</span>
                <span><strong>Tipo de Viagem:</strong> {tipoViagem === '0' ? 'Ida e Volta' : 'Somente Ida'}</span>
                {dhViagem && <span><strong>Data/Hora:</strong> {dhViagem.replace('T', ' ')}</span>}
                {itens[0]?.cidOrigemDes && <span><strong>Origem:</strong> {itens[0].cidOrigemDes}/{itens[0].cidOrigemUf}</span>}
                {itens[itens.length - 1]?.cidDestinoDes && <span><strong>Destino:</strong> {itens[itens.length - 1].cidDestinoDes}/{itens[itens.length - 1].cidDestinoUf}</span>}
                {veiPlaca && <span><strong>Placa:</strong> {veiPlaca}</span>}
                {qtdPassageiros && <span><strong>Passageiros:</strong> {qtdPassageiros}</span>}
                {estadosViagem && <span><strong>Estados:</strong> {estadosViagem}</span>}
                {descServico && <span><strong>Descrição:</strong> {descServico}</span>}
              </div>
            </div>

            {/* Itens */}
            <div style={secStyle}>
              <div style={labelStyle}>Itens</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead>
                  <tr className="thead-claro" style={{ background: '#f3f4f6' }}>
                    <th style={{ textAlign: 'left', padding: '0.3rem 0.4rem', fontWeight: 600 }}>Descrição</th>
                    <th style={{ textAlign: 'center', padding: '0.3rem 0.4rem', fontWeight: 600, width: 60 }}>Qtd</th>
                    <th style={{ textAlign: 'right', padding: '0.3rem 0.4rem', fontWeight: 600, width: 110 }}>Vl. Unit.</th>
                    <th style={{ textAlign: 'right', padding: '0.3rem 0.4rem', fontWeight: 600, width: 110 }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {itens.map((it, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '0.3rem 0.4rem' }}>
                        {it.descricao}
                        {it.complemento && <span style={{ color: '#9ca3af', fontSize: '0.75rem' }}> — {it.complemento}</span>}
                      </td>
                      <td style={{ textAlign: 'center', padding: '0.3rem 0.4rem' }}>{it.quantidade}</td>
                      <td style={{ textAlign: 'right', padding: '0.3rem 0.4rem' }}>{brl(it.valorUnitario)}</td>
                      <td style={{ textAlign: 'right', padding: '0.3rem 0.4rem', fontWeight: 600 }}>{brl(it.quantidade * it.valorUnitario)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Impostos + IBS/CBS */}
            {(impostosList.length > 0 || ibscbsList.length > 0) && (
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
                {impostosList.length > 0 && (
                  <div style={{ ...secStyle, flex: '1 1 200px', marginBottom: 0 }}>
                    <div style={labelStyle}>Impostos</div>
                    {impostosList.map(imp => (
                      <div key={imp.label} style={rowStyle}>
                        <span style={{ fontWeight: imp.retido ? 700 : 600, color: imp.retido ? '#dc2626' : undefined }}>{imp.label}</span>
                        <span style={{ color: '#dc2626', fontWeight: 700 }}>{brl(imp.valor)}</span>
                      </div>
                    ))}
                  </div>
                )}
                {ibscbsList.length > 0 && (
                  <div style={{ ...secStyle, flex: '1 1 200px', marginBottom: 0 }}>
                    <div style={labelStyle}>IBS / CBS</div>
                    {ibscbsList.map(imp => (
                      <div key={imp.label} style={rowStyle}>
                        <span style={{ fontWeight: 600 }}>{imp.label}</span>
                        <span style={{ color: '#1e40af', fontWeight: 700 }}>{brl(imp.valor)}</span>
                      </div>
                    ))}
                    {itens[0]?.ibsCbs?.cst && (
                      <div style={{ fontSize: '0.72rem', color: '#6b7280', marginTop: '0.3rem' }}>CST: {itens[0].ibsCbs.cst} · Classe: {itens[0].ibsCbs.classTrib}</div>
                    )}
                  </div>
                )}
              </div>
            )}

            {erro && <div className="login-error" style={{ marginTop: '1rem' }}>{erro}</div>}
            <div className="etapa-acoes">
              <button className="btn btn-secondary" onClick={() => setEtapa(3)}>← Voltar</button>
              <button
                onClick={() => {
                  const base = new Date(dataEmissao)
                  setFinParcelas(Array.from({ length: finQtdParcelas }, (_, i) => {
                    const d = new Date(base)
                    d.setDate(d.getDate() + finIntervalo * (i + 1))
                    return { numero: String(i + 1).padStart(2, '0'), vencimento: d.toISOString().slice(0, 10), valor: (totalItens / finQtdParcelas).toFixed(2) }
                  }))
                  setFinContaErro(null)
                  setFinanceiroModal(true)
                }}
                style={{ background: '#0f766e', color: '#fff', border: 'none', borderRadius: 6, padding: '0.35rem 0.9rem', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer' }}
              >
                {finParcelas.length > 0 ? 'Financeiro ✓' : '+ Financeiro'}
              </button>
              <button className="btn btn-orange" disabled={enviando || clienteSelecionado?.bloqueado} onClick={emitir}>{enviando ? 'Emitindo...' : `Emitir ${titulo}`}</button>
            </div>
          </div>
          )
        })()}

      </div>
    </div>

    {/* ── Modal Financeiro ── */}
    {financeiroModal && (
      <div style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        zIndex: 1000, padding: '24px 12px',
      }} onClick={() => setFinanceiroModal(false)}>
        <div style={{
          background: '#fff', borderRadius: 8, width: 560, maxWidth: '96vw',
          maxHeight: 'calc(100vh - 48px)', overflow: 'hidden', display: 'flex', flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        }} onClick={e => e.stopPropagation()}>
          <div style={{ background: '#0f766e', color: '#fff', padding: '0.85rem 1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                Financeiro
                {tipo === 'CTE' && (
                  <span style={{
                    fontSize: '0.7rem', fontWeight: 700, color: '#fff',
                    background: 'rgba(255,255,255,0.22)', padding: '1px 7px', borderRadius: 4,
                  }}>
                    TNOTFAT
                  </span>
                )}
              </div>
              <div style={{ fontSize: '0.78rem', opacity: 0.85, marginTop: 2 }}>
                Série {serie} · Nº {numero} — Total: {totalItens.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
              </div>
              {finCondDes && (
                <div style={{ fontSize: '0.75rem', opacity: 0.85, marginTop: 2 }}>
                  Condição de Pagamento: {finCondDes}
                </div>
              )}
            </div>
            <button onClick={() => setFinanceiroModal(false)} style={{ background: 'rgba(255,255,255,0.18)', border: 'none', cursor: 'pointer', color: '#fff', padding: '2px 8px', borderRadius: 4, fontSize: '1rem' }}>✕</button>
          </div>
          <div style={{ overflowY: 'auto', flex: 1, padding: '1rem 1.25rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', display: 'block', marginBottom: 3 }}>Qtd. Parcelas</label>
                <input type="number" min={1} max={36} value={finQtdParcelas}
                  onChange={e => {
                    const qtd = Math.max(1, Math.min(36, Number(e.target.value)))
                    setFinQtdParcelas(qtd)
                    const base = new Date(dataEmissao)
                    setFinParcelas(Array.from({ length: qtd }, (_, i) => {
                      const d = new Date(base)
                      d.setDate(d.getDate() + finIntervalo * (i + 1))
                      return { numero: String(i + 1).padStart(2, '0'), vencimento: d.toISOString().slice(0, 10), valor: (totalItens / qtd).toFixed(2) }
                    }))
                  }}
                  style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #d1d5db', borderRadius: 4, padding: '6px 8px', fontSize: '0.85rem' }} />
              </div>
              <div>
                <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', display: 'block', marginBottom: 3 }}>Intervalo (dias)</label>
                <input type="number" min={0} value={finIntervalo}
                  onChange={e => {
                    const intervalo = Math.max(0, Number(e.target.value))
                    setFinIntervalo(intervalo)
                    const base = new Date(dataEmissao)
                    setFinParcelas(Array.from({ length: finQtdParcelas }, (_, i) => {
                      const d = new Date(base)
                      d.setDate(d.getDate() + intervalo * (i + 1))
                      return { numero: String(i + 1).padStart(2, '0'), vencimento: d.toISOString().slice(0, 10), valor: (totalItens / finQtdParcelas).toFixed(2) }
                    }))
                  }}
                  style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #d1d5db', borderRadius: 4, padding: '6px 8px', fontSize: '0.85rem' }} />
              </div>
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', display: 'block', marginBottom: 3 }}>
                Conta Financeira {finContaErro && <span style={{ color: '#dc2626' }}>*</span>}
              </label>
              <div style={{ display: 'flex', gap: '0.5rem', position: 'relative' }}>
                <input type="text" value={finContaCod}
                  onChange={e => { setFinContaCod(e.target.value); setFinContaNome(''); setFinContaErro(null) }}
                  onBlur={async e => {
                    const cod = e.target.value.trim()
                    if (!cod) return
                    const r = await api.get('/erp/contas', { params: { busca: cod } })
                    const found = r.data.find((c: any) => c.dep_cod.trim() === cod)
                    if (found) setFinContaNome(found.dep_des)
                  }}
                  placeholder="Código" autoComplete="off"
                  style={{ width: 80, border: '1px solid #d1d5db', borderRadius: 4, padding: '6px 8px', fontSize: '0.85rem' }} />
                <div style={{ flex: 1, position: 'relative' }}>
                  <input type="text" value={finContaNome}
                    onChange={async e => {
                      const v = e.target.value
                      setFinContaNome(v)
                      setFinContaCod('')
                      setFinContaErro(null)
                      if (v.length >= 2) {
                        const r = await api.get('/erp/contas', { params: { busca: v } })
                        setFinContaResultados(r.data)
                        setFinContaAberto(true)
                      } else {
                        setFinContaResultados([])
                        setFinContaAberto(false)
                      }
                    }}
                    onBlur={() => setTimeout(() => setFinContaAberto(false), 150)}
                    placeholder="Nome da conta" autoComplete="off"
                    style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #d1d5db', borderRadius: 4, padding: '6px 8px', fontSize: '0.85rem' }} />
                  {finContaAberto && finContaResultados.length > 0 && (
                    <div style={{ position: 'absolute', zIndex: 50, background: '#fff', border: '1px solid #d1d5db', borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', width: '100%', maxHeight: 200, overflowY: 'auto', top: '100%', left: 0 }}>
                      {finContaResultados.map(c => (
                        <div key={c.dep_cod} onMouseDown={() => { setFinContaCod(c.dep_cod); setFinContaNome(c.dep_des); setFinContaAberto(false) }}
                          style={{ padding: '7px 10px', cursor: 'pointer', fontSize: '0.82rem', borderBottom: '1px solid #f3f4f6' }}
                          onMouseEnter={e => (e.currentTarget.style.background = '#f0fdf4')}
                          onMouseLeave={e => (e.currentTarget.style.background = '')}>
                          <span style={{ fontFamily: 'monospace', fontWeight: 700, marginRight: 8, color: '#374151' }}>{c.dep_cod}</span>
                          <span style={{ color: '#6b7280' }}>{c.dep_des}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {finContaErro && <div style={{ color: '#dc2626', fontSize: '0.75rem', marginTop: 4 }}>{finContaErro}</div>}
            </div>
            {finParcelas.length > 0 && (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                <thead>
                  <tr className="thead-claro" style={{ background: '#f9fafb' }}>
                    <th style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'center', width: 60 }}>Parcela</th>
                    <th style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'left' }}>Vencimento</th>
                    <th style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'right' }}>Valor (R$)</th>
                  </tr>
                </thead>
                <tbody>
                  {finParcelas.map((p, i) => (
                    <tr key={i}>
                      <td style={{ padding: '5px 8px', border: '1px solid #e5e7eb', textAlign: 'center', fontWeight: 700, color: '#6b7280' }}>{p.numero}</td>
                      <td style={{ padding: '4px 8px', border: '1px solid #e5e7eb' }}>
                        <input type="date" value={p.vencimento}
                          onChange={e => setFinParcelas(prev => prev.map((x, j) => j === i ? { ...x, vencimento: limitarAno4Digitos(x.vencimento, e.target.value) } : x))}
                          style={{ border: 'none', background: 'transparent', fontSize: '0.82rem', width: '100%' }} />
                      </td>
                      <td style={{ padding: '4px 8px', border: '1px solid #e5e7eb' }}>
                        <input type="number" value={p.valor}
                          onChange={e => setFinParcelas(prev => prev.map((x, j) => j === i ? { ...x, valor: e.target.value } : x))}
                          style={{ border: 'none', background: 'transparent', fontSize: '0.82rem', textAlign: 'right', width: '100%' }} />
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="thead-claro" style={{ background: '#f9fafb', fontWeight: 700 }}>
                    <td colSpan={2} style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'right' }}>Total</td>
                    <td style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'right', color: '#0f766e' }}>
                      {finParcelas.reduce((s, p) => s + Number(p.valor), 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                </tfoot>
              </table>
            )}
          </div>
          <div style={{ borderTop: '1px solid #e5e7eb', padding: '0.65rem 1.25rem', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', background: '#f9fafb' }}>
            <button onClick={() => { setFinanceiroModal(false); setFinContaErro(null) }} style={{ padding: '5px 16px', background: '#fff', border: '1px solid #d1d5db', borderRadius: 4, fontSize: '0.82rem', cursor: 'pointer', color: '#6b7280' }}>Cancelar</button>
            <button
              onClick={() => {
                if (!finContaCod.trim()) { setFinContaErro('Informe a conta financeira'); return }
                setFinanceiroModal(false)
              }}
              style={{ padding: '5px 18px', background: '#0f766e', color: '#fff', border: 'none', borderRadius: 4, fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer' }}>
              Confirmar
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  )
}
